// Geometry document reducer — extracted from editorStore.ts.
import {
  assembleDrawingDocument,
  assembleDrawingDocumentInPlace,
  drawingDocumentSchema,
  patchEntityById,
  recomputeNativeClipPath,
  type DrawingDocument,
  type DrawingEntity,
  type DrawingEntityInput,
  type ViewportEntity,
} from "@dxf-ai-editor/drawing-model";
import {
  parseArcCommandInput,
  parseCircleCommandInput,
  parseLineCommandInput,
  parsePointCommandInput,
} from "@dxf-ai-editor/coordinate-input";
import type {
  DynamicBlockCommand,
  EditorCommand,
  EditorShellState,
  EntityPreview,
} from "@dxf-ai-editor/contracts";
import {
  // DEFUSE S15 — the typed property-edit WRITER replaces the string-round-trip
  // `applySimpleGeometryPropertyChanges` (+ reconcile scaffolds) for the
  // Line/Circle/Arc geometry document write; raw precision / Z-preserve now hold
  // by construction (the writer edits the typed document field in place).
  buildTypedSimpleGeometryEdit,
  // DEFUSE S15 common-props cutover — the 6 non-Color common fields
  // (Layer/Linetype/Lineweight/Transparency/Linetype Scale/Thickness) route
  // through the typed writer instead of the legacy string-bag common merge.
  buildTypedCommonEdit,
  // DEFUSE S15 Color cutover — Color (a typed EntityColor union) routes through
  // its OWN dedicated typed writer (deserializeColor toRaw, lossless `!EC:`) so
  // the lossy-vs-lossless suppression block is subsumed by the no-op semantics.
  buildTypedColorEdit,
  // DEFUSE S15 Slice A — Rectangular Associative Array + LWPOLYLINE special
  // geometry routes through the typed writer; the legacy propertyTranslator
  // branches for these two kinds are DELETED. The strip removes the writer-owned
  // labels from the translator's change set (no double-write path).
  buildTypedExtendedGeometryEdit,
  // DEFUSE S15 Slice E — the side-effect routing PIVOT: the Hatch Pattern edit
  // (the ONLY writable side-effect leaf; Solid is a derived read-only display)
  // routes through the mutator registry (`applyHatchPatternMutation`), surfaced by
  // `buildTypedExtendedGeometrySideEffectPatch`. The legacy propertyTranslator
  // HATCH branch derivation + the hand-rolled hatchedit rawPatch reconcile are
  // DELETED — both now converge on the ONE coupled-field mutator.
  buildTypedExtendedGeometrySideEffectPatch,
  // @codex PR #794 round 14 P2 — the CAD rule for MTEXT's cached layout
  // measurement (DXF groups 42/43): which schema fields invalidate it, and the
  // prev→next diff the `properties/apply` post-steps normalization applies.
  mtextLayoutMeasurementInvalidation,
  applyHatchPatternMutation,
  type HatchPatternMutation,
  stripTypedExtendedGeometryChanges,
  TYPED_COMMON_CUTOVER_LABELS,
  canApplyProperty,
  type TypedFieldPatch,
  isCommonObjectProperty,
  mergePropertyValueMap,
  applyPurgeBlocks,
  renameBlockMutation,
  xattachMutation,
  xbindMutation,
  xunloadMutation,
  xreloadMutation,
  xdetachMutation,
  xclipMutation,
  applySetInsertionBase,
  applySetCeColor,
  resolveRevcloudObjectSource,
} from "@dxf-ai-editor/command-engine";
import type {
  BlockChildEntity,
  HatchEntity,
  HatchGradient,
  HatchPatternLine,
  XrefClipBoundary,
} from "@dxf-ai-editor/drawing-model";
import {
  translateExtendedEntityPropertyChanges,
  stripTypedCommonCutoverChanges,
  stripTypedColorChange,
} from "./propertyTranslator";
import {
  isGripDragPayload,
  resolveGripDragDocumentEntity,
  translateGripDrag,
} from "./gripDragTranslate";
import {
  handleArrayEdit,
  handleCreateArrayPath,
  handleCreateArrayPolar,
  handleCreateArrayRect,
} from "./arrayReducers";
import {
  createBlockDefinitionInDocument,
  explodeBlockInsertInDocument,
  type CreateBlockDefinitionCommandInput,
} from "./blockReducerHelpers";
import {
  dispatchBlockEditCommand,
  syncBlockEditSpaceToDefinition,
} from "./blockEditReducer";
import {
  handleCreateAttdef,
  handleUpdateAttributeValue,
  handleUpdateAttributeValues,
  handleRemoveAttdef,
  handleUpdateAttdef,
  handleRenameAttdefTag,
  handleReorderAttdefs,
  handleAttsync,
} from "./attributeReducerHelpers";
import {
  handleAddActionToChainGroup,
  handleAddDynamicAction,
  handleAddDynamicParameter,
  handleAddGeometricConstraint,
  handleAddVisibilityState,
  handleBvhide,
  handleBvshow,
  handleBvshowAll,
  handleCreateChainGroup,
  handleDeleteVisibilityState,
  handleRemoveActionFromChain,
  handleRemoveDynamicAction,
  handleRemoveDynamicParameter,
  handleRenameVisibilityState,
  handleReorderChainGroup,
  handleReorderVisibilityStates,
  handleSetCurrentVisibilityState,
  handleSetDefaultVisibilityState,
  handleToggleVisibilityStateEntity,
  handleUpdateDynamicAction,
  handleUpdateDynamicParameter,
} from "./dynamicAuthoringReducerHelpers";
import {
  handleAddBlockProperty,
  handleCreateLookupTable,
  handleDeleteLookupTable,
  handleRemoveBlockProperty,
  handleReorderBlockProperties,
  handleSetBlockPropertiesTable,
  handleUpdateBlockProperty,
  handleUpdateLookupTable,
} from "./dynamicLookupReducerHelpers";
import {
  handleRenameBlockAction,
  handleRenameBlockParameter,
  handleReorderBlockActions,
  handleReorderBlockParameters,
  handleToggleParameterShowInPalette,
  handleUpdateBlockActionDescription,
  handleUpdateBlockParameterDescription,
} from "./blockPropertiesManagerReducerHelpers";
// PR-0 decomposition — sub-reducers handle disjoint slices of the
// 96-case switch. Each `applyXxxAction` returns `null` when the action
// is not in its scope, signalling the chain to fall through to the
// next sub-reducer (or finally the default no-op).
//
// Phase 3b PR #5 — switched callers in `reduceGeometryInner` to the
// `*WithMutations` entry points so the dispatch chain returns a
// `ReducerMutationResult`. Phase 6 (Track 1 cleanup) removed the legacy
// thin wrappers (`applyAnnotativeAction`/`applyEditAction`/
// `applyEditTrimAction`) entirely — `*WithMutations` are the sole entry
// points.
import { applyAnnotativeActionWithMutations } from "./geometryReducerAnnotative";
import { applyEditActionWithMutations } from "./geometryReducerEdit";
import { applyEditTrimActionWithMutations } from "./geometryReducerEditTrim";
import { applyLayoutAction } from "./geometryReducerLayout";
import { applyStyleTablesActionWithMutations } from "./styleTablesReducer";
import {
  diffDocumentMutations,
  documentWithoutSyntheticSpaces,
  replaceEntitySnapshots,
  singleAddedMutation,
} from "./geometryMutationHelpers";
import {
  NO_MUTATIONS,
  type EntityMutation,
  type ReducerMutationResult,
} from "./types";

export type GeometryCreateCommand = Extract<
  EditorCommand,
  {
    type:
      | "geometry/create-line"
      | "geometry/create-point"
      | "geometry/create-circle"
      | "geometry/create-arc"
      | "geometry/create-ellipse"
      | "geometry/create-spline"
      | "geometry/create-lwpolyline"
      | "geometry/create-donut"
      | "geometry/create-text"
      | "geometry/create-mtext"
      | "geometry/create-dimension"
      | "geometry/create-multileader"
      | "geometry/create-revcloud"
      | "geometry/create-wipeout"
      | "geometry/create-hatch"
      | "geometry/create-block-insert"
      // T6b — AI-dispatch coalescing batch sibling of create-block-insert
      // (see the reducer case + `aiSafeContour.ts`'s coalescer).
      | "geometry/create-block-inserts-batch"
      // CECOLOR (EPIC-COLOR-UNION round-6 FINDING A) — an ATTDEF authored in
      // BEDIT is a DRAWN entity. Listing it here (the typed create-command
      // inventory) lets `resolveGeometryDocumentCommand` route it through the
      // dispatch chokepoint + `stampCeColorOntoCreateCommand` like every other
      // create, instead of the stamp being dead (the round-5 reducer stamp was
      // never reached via the store because ATTDEF was absent from the router).
      | "geometry/create-attdef"
      | "geometry/create-viewport"
      | "geometry/create-block-definition"
      | "geometry/explode-insert";
  }
>;

// `appendGeometryEntity`, `nextGeometryEntityId` and
// `removeGeometryEntitiesById` live in `geometryReducerHelpers.ts` so
// sub-reducers can import them without re-importing this module (was
// the source of the depcruise cycles `geometryReducer.ts` ↔
// arrayReducers / geometryReducerEdit / geometryReducerEditTrim /
// geometryReducerLayout). Re-exported for ergonomic back-compat.
export {
  appendGeometryEntity,
  appendGeometryEntities,
  buildBlockInsertEntityInput,
  createEntityIdAllocator,
  nextGeometryEntityId,
  removeGeometryEntitiesById,
  clearDanglingViewportClips,
  recomputeObjectDerivedViewportClips,
} from "./geometryReducerHelpers";

import {
  appendGeometryEntity,
  appendGeometryEntities,
  buildBlockInsertEntityInput,
  createEntityIdAllocator,
  nextGeometryEntityId,
  removeGeometryEntitiesById,
  clearDanglingViewportClips,
  recomputeObjectDerivedViewportClips,
} from "./geometryReducerHelpers";

export function resolveGeometryCommandFromText(
  state: EditorShellState,
  rawText: string,
  source: EditorCommand["source"],
): { command: GeometryCreateCommand | null; error: string | null } {
  const [commandName, ...rest] = rawText.trim().split(/\s+/u);
  const commandArgs = rest.join(" ");
  const spaceId = state.drawing.activeSpaceId;
  switch (commandName?.toUpperCase()) {
    case "LINE": {
      const parsed = parseLineCommandInput(commandArgs);
      if (!parsed.ok) {
        return { command: null, error: "LINE expects `x,y x,y` coordinates." };
      }
      return {
        command: {
          type: "geometry/create-line",
          source,
          spaceId,
          start: parsed.value.start,
          end: parsed.value.end,
        },
        error: null,
      };
    }
    case "POINT": {
      const parsed = parsePointCommandInput(commandArgs);
      if (!parsed.ok) {
        return { command: null, error: "POINT expects `x,y` coordinates." };
      }
      return {
        command: {
          type: "geometry/create-point",
          source,
          spaceId,
          location: parsed.value.location,
          rotation: 0,
        },
        error: null,
      };
    }
    case "CIRCLE": {
      const parsed = parseCircleCommandInput(commandArgs);
      if (!parsed.ok) {
        return { command: null, error: "CIRCLE expects `x,y radius`." };
      }
      return {
        command: {
          type: "geometry/create-circle",
          source,
          spaceId,
          center: parsed.value.center,
          radius: parsed.value.radius,
        },
        error: null,
      };
    }
    case "ARC": {
      const parsed = parseArcCommandInput(commandArgs);
      if (!parsed.ok) {
        return {
          command: null,
          error: "ARC expects `x,y radius startAngle endAngle`.",
        };
      }
      return {
        command: {
          type: "geometry/create-arc",
          source,
          spaceId,
          center: parsed.value.center,
          radius: parsed.value.radius,
          startAngle: parsed.value.startAngle,
          endAngle: parsed.value.endAngle,
        },
        error: null,
      };
    }
    default:
      return { command: null, error: null };
  }
}

/**
 * PR #5 — small adaptor for the ~20 inline create-* cases. Appends the
 * supplied entity to the document, then synthesises a single-added
 * `EntityMutation` against the post-assemble doc so the reference
 * matches what `commitMutation` will install in the cache (Zod parse
 * breaks reference identity).
 */
function appendAndBuildMutation(
  document: DrawingDocument,
  entity: DrawingEntityInput,
): ReducerMutationResult {
  const next = appendGeometryEntity(document, entity);
  return {
    document: next,
    mutations: singleAddedMutation(next, entity.spaceId, entity.id),
  };
}

/**
 * PR #5 — wrap a helper that returned a bare `DrawingDocument` into
 * a `ReducerMutationResult` by diffing the pre/post documents. Used
 * for cases (purge-blocks, rename-block, xref family, multileader-edit
 * family, attribute helpers, dynamic-block authoring helpers, BPM,
 * lookup tables, array, properties/apply, shell/delete-entities) where
 * there is no closed-form predicate for what changed and the helper
 * already paid the O(N) `assembleDrawingDocument` cost.
 *
 * `styleTablesTouched` is forwarded through; callers pass it for cases
 * that mutate layers / dim styles / mleader styles / text styles.
 */
function wrapHelperResult(
  prev: DrawingDocument,
  next: DrawingDocument,
  styleTablesTouched?: ReducerMutationResult["mutations"]["styleTablesTouched"],
): ReducerMutationResult {
  return {
    document: next,
    mutations: diffDocumentMutations(prev, next, styleTablesTouched),
  };
}

function applySimpleGeometryPatch(
  document: DrawingDocument,
  patch: TypedFieldPatch | null,
): DrawingDocument {
  if (!patch) {
    return document;
  }

  // `patchEntityById` reuses the existing entity's `id` and `spaceId`
  // (helpers.ts line 130-131), so invariants cannot drift. The changes
  // payload originated from a typed property descriptor — shape is
  // validated upstream. This path runs per-property-keystroke in the
  // Properties palette.
  //
  // T6c incremental validation: a single-entity in-place field patch — no
  // entity added/removed, id/spaceId unchanged by construction → the cross-
  // entity invariants are preserved, so route to the fast-path assembler with
  // the patched entity's own id. The per-entity Zod field check STILL runs in
  // dev/test (property edits translate string payloads → typed patches, so the
  // schema check must survive — @codex #390-F4); only the whole-document walk
  // is dropped.
  const patched = patchEntityById(document, patch.entityId, patch.changes);
  return assembleDrawingDocumentInPlace(patched, [patch.entityId]);
}

// DEFUSE S15 — `reconcileLinePatchWithDocument` / `reconcileCircleArcPatchWithDocument`
// DELETED. They existed ONLY to un-round the patch that
// `applySimpleGeometryPropertyChanges` built from the 3-decimal `EntityPreview`
// preview string (sourcing untouched X/Y/Z raw from the live document). The S15
// typed writer (`buildTypedSimpleGeometryEdit`) edits the typed document entity
// field IN PLACE, so every untouched axis keeps its full document precision +
// group-30/31 Z by construction — there is nothing to reconcile. The raw-precision
// / Z-preserve invariant (#317 residual R2) is now structural, proven by the
// command-engine parity gate (`typedPropertyWriter.parity.test.ts`, PART A2). The
// `reconcilePointAgainstDocument` one-truth these called was likewise removed from
// `geometryEditRawPoint.ts` (its only consumers).

// PR-0 decomposition — the local 2-arg `lineLineIntersection` helper
// (formerly here) shadowed the 4-arg import from
// `@dxf-ai-editor/command-engine`. Per deps.md risk #1 it has been
// duplicated into the two consumer files
// (`geometryReducerEdit.ts` for OFFSET, `geometryReducerEditTrim.ts`
// for TRIM/EXTEND) under the same name to preserve the original
// runtime resolution byte-for-byte. The shadow bug fix is tracked as
// a follow-up ticket.

// Phase 2.3 — BEDIT / BSAVE / BCLOSE lifecycle is owned by
// blockEditReducer. Other commands fall through to the main switch;
// their result is piped through syncBlockEditSpaceToDefinition so
// mutations during a BEDIT session are mirrored back to the block.
//
// PR #362 round-6 (F5) — `SYNTHETIC_EDIT_SPACE_KINDS` +
// `documentWithoutSyntheticSpaces` were relocated to `geometryMutationHelpers.ts`
// (alongside `diffDocumentMutations`) so the forward `space/*` diff here AND the
// undo/redo/abort `computeRevisionStateDiff` in `editorStore.ts` share ONE source
// of truth for the synthetic-space exclusion.

// Phase 3b PR #5 — `reduceGeometryDocumentWithMutations` is the new
// canonical entry point. It returns `ReducerMutationResult` (or `null`
// for unrecognised commands) so the dispatch layer can apply
// incremental cache + index updates in O(|touched|). The legacy
// `reduceGeometryDocument` is kept as a thin `.document`-only wrapper
// so `editorStore.ts` (and many existing tests) keep working until
// PR #6 swaps the dispatch flow.
export function reduceGeometryDocumentWithMutations(
  geometryDocument: DrawingDocument,
  command: EditorCommand | DynamicBlockCommand,
  previewEntities: EntityPreview[] = [],
  // Codex P2 (#515 round 2, post-merge remediation of #493) — the ONE-TRUTH
  // typed-entity resolver the grip RENDER + the shell `properties/apply`
  // path (`propertyReducer.ts`) use (`getEntityById`, compact-cache first).
  // Injected (not imported — geometryReducer.ts is store-free to avoid an
  // editorStore import cycle) so the PERSISTED document reducer's
  // `properties/apply` case resolves the SAME `preMutationDocEntity` the
  // shell reducer resolves for the identical command, via the shared
  // `resolveGripDragDocumentEntity` (gripDragTranslate.ts): only used as a
  // fallback when the `geometryDocument.spaces` linear scan misses (a
  // large/streamed import whose `spaces` is slimmed but the compact cache
  // still holds the entity). Without this, a slimmed-spaces grip drag could
  // leave the shell showing the typed-frame angle while the document
  // reducer — scanning alone — missed the entity entirely and persisted a
  // DIFFERENT (or no) geometry patch, diverging the Properties panel from
  // the geometry document / export / undo.
  resolveDocumentEntity?: (id: string) => DrawingEntity | undefined,
): ReducerMutationResult | null {
  if (command.type.startsWith("space/")) {
    // Block-edit / refedit lifecycle commands rebuild large slices of
    // the document (synthetic block-edit space, refedit working set).
    // We don't have a closed-form predicate for the affected entity
    // ids; fall back to a document diff.
    const next = dispatchBlockEditCommand(
      geometryDocument,
      command as Parameters<typeof dispatchBlockEditCommand>[1],
    );
    // PR #362 round-5 (F5 ROOT) — entering/exiting BEDIT/REFEDIT ADDS/REMOVES a
    // SYNTHETIC edit space (`block-edit`/`block-test`/`refedit`) populated with
    // COPIES of the block's entities. A raw `diffDocumentMutations` sees those
    // copied entities as `added` (enter) / `removed` (exit) → a SPURIOUS geometry
    // mutation, but a BEDIT enter/exit is a CONTEXT switch, not a user geometry
    // edit. EXCLUDE the synthetic edit spaces from the geometry-class diff (the
    // same way `documentTableFingerprint` excludes them from the TABLE diff), so
    // enter/exit-block-edit bumps CONTEXT only. The block-DEFINITION content a
    // BEDIT session actually writes lands in `blocks[name].entities` (kept), so a
    // real block-definition edit (save-block-edit / refedit working-set) still
    // bumps TABLE via `docHeaderChanged`; and an actual entity edit issued WHILE
    // in the session is a `geometry/*` command (NOT this `space/` path) — it adds
    // to the synthetic space, is captured by the inner reducer's own mutation,
    // and is mirrored into `blocks[name].entities`, so it still bumps geometry.
    return {
      document: next,
      mutations: diffDocumentMutations(
        documentWithoutSyntheticSpaces(geometryDocument),
        documentWithoutSyntheticSpaces(next),
      ),
    };
  }
  const innerResult = reduceGeometryInnerWithMutations(
    geometryDocument,
    command,
    previewEntities,
    resolveDocumentEntity,
  );
  if (innerResult === null) return null;
  // BEDIT mirror runs after every non-space command. It either no-ops
  // (no active block-edit session) or pushes the new entity state from
  // the synthetic space back into `blocks[name].entities`. Any extra
  // mutation introduced by the mirror is captured by re-diffing if the
  // mirror produced a different document reference.
  const mirrored = syncBlockEditSpaceToDefinition(innerResult.document);
  let base: ReducerMutationResult;
  if (mirrored === innerResult.document) {
    base = innerResult;
  } else {
    // Mirror touched the doc — fold its diff into the inner mutations.
    // In practice the mirror only writes through to blockDef.entities; no
    // entity ids change in the spaces. The diff will surface those
    // shape-only differences (e.g. `blocks` map identity flip) as nothing
    // entity-level, so this fold is safe.
    const mirrorDiff = diffDocumentMutations(innerResult.document, mirrored);
    base =
      mirrorDiff.added.length === 0 &&
      mirrorDiff.removed.length === 0 &&
      mirrorDiff.updated.length === 0
        ? { document: mirrored, mutations: innerResult.mutations }
        : {
            document: mirrored,
            mutations: mergeMutations(innerResult.mutations, mirrorDiff),
          };
  }
  // @codex #496 P2 — a boundary OBJECT edit/move (grip drag, STRETCH, Properties
  // vertex edit, …) that changed the geometry of an entity a FROZEN object-mode
  // viewport clip points at (its 340 `clippingBoundaryHandle`) must RE-DERIVE that
  // clip, or the in-session frozen `clipBoundaryPaperSpace`/`clipPath` silently
  // diverges from the 340-driven export→reopen (the reopened file re-resolves the
  // EDITED object). The EDIT/MOVE sibling of the delete-side
  // `clearDanglingViewportClips`; keyed on the SAME `mutations.updated` set every
  // edit path funnels through here, so no per-command wiring (incl. the
  // `properties/apply` grip / palette path) is missed. No-ops (same doc ref) when
  // no changed entity is a frozen clip's boundary — the overwhelmingly common case.
  if (base.mutations.updated.length === 0) {
    return base;
  }
  const { document: reconciledDoc, updated: clipDiffs } =
    recomputeObjectDerivedViewportClips(
      base.document,
      base.mutations.updated.map((u) => ({ spaceId: u.spaceId, id: u.next.id })),
    );
  if (clipDiffs.length === 0) {
    return base;
  }
  return {
    document: reconciledDoc,
    mutations: mergeMutations(base.mutations, {
      added: [],
      removed: [],
      updated: clipDiffs,
      styleTablesTouched: NO_MUTATIONS.styleTablesTouched,
    }),
  };
}

/**
 * Legacy entry point — thin `.document`-only wrapper around
 * `reduceGeometryDocumentWithMutations`. Returns the input document
 * untouched when the command is not recognised by any sub-reducer.
 *
 * `editorStore.ts` still depends on this signature; PR #6 swaps that
 * call site to the `*WithMutations` variant and threads the diff into
 * `commitMutation`.
 */
export function reduceGeometryDocument(
  geometryDocument: DrawingDocument,
  command: EditorCommand | DynamicBlockCommand,
  previewEntities: EntityPreview[] = [],
): DrawingDocument {
  const result = reduceGeometryDocumentWithMutations(
    geometryDocument,
    command,
    previewEntities,
  );
  return result?.document ?? geometryDocument;
}

/**
 * Concatenate two `EntityMutation` records. Style-table touch sets are
 * unioned. Order is preserved (a then b).
 */
function mergeMutations(a: EntityMutation, b: EntityMutation): EntityMutation {
  if (a === NO_MUTATIONS) return b;
  if (b === NO_MUTATIONS) return a;
  const styleTables = new Set(a.styleTablesTouched);
  for (const k of b.styleTablesTouched) styleTables.add(k);
  return {
    added: [...a.added, ...b.added],
    removed: [...a.removed, ...b.removed],
    updated: [...a.updated, ...b.updated],
    styleTablesTouched: styleTables,
  };
}

function reduceGeometryInnerWithMutations(
  geometryDocument: DrawingDocument,
  command: EditorCommand | DynamicBlockCommand,
  previewEntities: EntityPreview[] = [],
  // Codex P2 (#515 round 2) — see `reduceGeometryDocumentWithMutations`'s
  // param doc; threaded straight into the `properties/apply` case below,
  // the only site that resolves a `preMutationDocEntity`-by-id lookup.
  resolveDocumentEntity?: (id: string) => DrawingEntity | undefined,
): ReducerMutationResult | null {
  // PR-0 decomposition — try each sub-reducer in turn. Each returns
  // `null` when the action is not in its scope. The order matches the
  // pre-split case order (annotative cases were last in the original
  // switch but they are checked first here so the shell switch keeps
  // its original layout for the cases it still owns). This ordering
  // is irrelevant to correctness because the sub-reducers handle
  // disjoint sets of action types.
  //
  // PR #5 — call the `*WithMutations` variants directly so the
  // mutation diff propagates without being lost.
  const fromAnnotative = applyAnnotativeActionWithMutations(
    geometryDocument,
    command,
  );
  if (fromAnnotative !== null) return fromAnnotative;
  const fromEdit = applyEditActionWithMutations(geometryDocument, command);
  if (fromEdit !== null) return fromEdit;
  const fromEditTrim = applyEditTrimActionWithMutations(
    geometryDocument,
    command,
  );
  if (fromEditTrim !== null) return fromEditTrim;
  // Phase 5 — style/layer entity-remap commands
  // (header/delete-dimension-style / header/rename-dimension-style /
  //  header/delete-multileader-style / header/delete-layer). Returns
  // a `ReducerMutationResult` with `updated` mutations for entities
  // whose styleName/layer was remapped + a styleTablesTouched flag.
  const fromStyleTables = applyStyleTablesActionWithMutations(
    geometryDocument,
    command,
  );
  if (fromStyleTables !== null) return fromStyleTables;
  const fromLayout = applyLayoutAction(geometryDocument, command);
  if (fromLayout !== null) {
    // applyLayoutAction returns a bare DrawingDocument — only
    // `geometry/create-viewport` lands here today and it is a single
    // append, so we can synthesise a single-added mutation by diffing
    // the docs.
    return {
      document: fromLayout,
      mutations: diffDocumentMutations(geometryDocument, fromLayout),
    };
  }

  switch (command.type) {
    // BASE / БАЗА — set the drawing insertion base point ($INSBASE). The
    // mutation goes through the Command Engine (`applySetInsertionBase`); undo
    // is handled by `computeInverse` (header/set-insertion-base ↔ prior point).
    // Document-level header field only — no entity payload changes, so we
    // report NO_MUTATIONS. The `assembleDrawingDocument` wrap re-validates the
    // shape (matching the cannoscale / annotative header handlers).
    case "header/set-insertion-base": {
      const document = assembleDrawingDocument(
        applySetInsertionBase(geometryDocument, command.point),
      );
      return { document, mutations: NO_MUTATIONS };
    }

    // CECOLOR / COLOR / ЦВЕТ — set the current entity colour ($CECOLOR). The
    // mutation goes through the Command Engine (`applySetCeColor`); undo is
    // handled by `computeInverse` (header/set-cecolor ↔ prior colour). Document-
    // level header field only — no entity payload changes, so NO_MUTATIONS. The
    // `assembleDrawingDocument` wrap re-validates the shape (matching the
    // set-insertion-base / cannoscale / annotative header handlers).
    case "header/set-cecolor": {
      const document = assembleDrawingDocument(
        applySetCeColor(geometryDocument, command.color),
      );
      return { document, mutations: NO_MUTATIONS };
    }

    case "geometry/create-line": {
      return appendAndBuildMutation(geometryDocument, {
        id: nextGeometryEntityId(geometryDocument, "line"),
        type: "Line",
        spaceId: command.spaceId,
        start: {
          x: command.start.x,
          y: command.start.y,
        },
        end: {
          x: command.end.x,
          y: command.end.y,
        },
        // CECOLOR — inherit the current colour stamped at the dispatch boundary
        // (default ByLayer when the command omits it — legacy / tests).
        layer: command.layer ?? "0",
        color: command.color ?? { kind: "byLayer" },
      });
    }

    case "geometry/create-point": {
      return appendAndBuildMutation(geometryDocument, {
        id: nextGeometryEntityId(geometryDocument, "point"),
        type: "Point",
        spaceId: command.spaceId,
        location: {
          x: command.location.x,
          y: command.location.y,
        },
        rotation: command.rotation,
        layer: command.layer ?? "0",
        color: command.color ?? { kind: "byLayer" },
      });
    }

    case "geometry/create-circle": {
      return appendAndBuildMutation(geometryDocument, {
        id: nextGeometryEntityId(geometryDocument, "circle"),
        type: "Circle",
        spaceId: command.spaceId,
        center: {
          x: command.center.x,
          y: command.center.y,
        },
        radius: command.radius,
        layer: command.layer ?? "0",
        color: command.color ?? { kind: "byLayer" },
      });
    }

    case "geometry/create-arc": {
      return appendAndBuildMutation(geometryDocument, {
        id: nextGeometryEntityId(geometryDocument, "arc"),
        type: "Arc",
        spaceId: command.spaceId,
        center: {
          x: command.center.x,
          y: command.center.y,
        },
        radius: command.radius,
        startAngle: command.startAngle,
        endAngle: command.endAngle,
        layer: command.layer ?? "0",
        color: command.color ?? { kind: "byLayer" },
      });
    }

    case "geometry/create-ellipse": {
      // Convert majorAxisEnd (absolute world point picked by the user)
      // into the DXF-relative major-axis vector and derive the
      // minor / major ratio from the typed minor distance. AutoCAD
      // semantics: when the user-typed minor distance exceeds the
      // major-axis length, AutoCAD swaps roles so the major axis is
      // always the longer one. Mirror that behaviour here so the entity
      // round-trips through DXF with ratio in the canonical (0, 1] range.
      const majorVx = command.majorAxisEnd.x - command.center.x;
      const majorVy = command.majorAxisEnd.y - command.center.y;
      const majorLen = Math.hypot(majorVx, majorVy);
      const minorLen = Math.max(command.minorDistance, 1e-9);
      let majorAxisEndpoint = { x: majorVx, y: majorVy };
      let ratio: number;
      if (majorLen <= 1e-9) {
        // Degenerate pick (center == majorEnd). Fall back to a 1-unit
        // X-axis major + minor=ratio to avoid NaN; the user can edit
        // through the property panel afterwards.
        majorAxisEndpoint = { x: 1, y: 0 };
        ratio = Math.min(1, minorLen);
      } else if (minorLen > majorLen) {
        // Swap: rotate the major axis 90° CCW and scale to current
        // minorLen so the new major-axis is the longer one. The new
        // ratio = old major / new major.
        const swappedX = -majorVy * (minorLen / majorLen);
        const swappedY = majorVx * (minorLen / majorLen);
        majorAxisEndpoint = { x: swappedX, y: swappedY };
        ratio = majorLen / minorLen;
      } else {
        ratio = minorLen / majorLen;
      }
      return appendAndBuildMutation(geometryDocument, {
        id: nextGeometryEntityId(geometryDocument, "ellipse"),
        type: "Ellipse",
        spaceId: command.spaceId,
        center: {
          x: command.center.x,
          y: command.center.y,
        },
        majorAxisEndpoint,
        ratio,
        startParam: command.startParam,
        endParam: command.endParam,
        extrusion: { x: 0, y: 0, z: 1 },
        thickness: 0,
        layer: command.layer ?? "0",
        color: command.color ?? { kind: "byLayer" },
      });
    }

    case "geometry/create-spline": {
      const fitPoints = (command.fitPoints ?? []).map((p) => ({
        x: p.x,
        y: p.y,
        z: p.z ?? 0,
      }));
      const controlPoints = (command.controlPoints ?? []).map((p) => ({
        x: p.x,
        y: p.y,
        z: p.z ?? 0,
      }));
      const knots = command.knots ?? [];
      const weights = command.weights ?? [];
      return appendAndBuildMutation(geometryDocument, {
        id: nextGeometryEntityId(geometryDocument, "spline"),
        type: "Spline",
        spaceId: command.spaceId,
        degree: command.degree ?? 3,
        closed: command.closed ?? false,
        periodic: false,
        rational: command.rational ?? false,
        planar: true,
        fitPoints,
        controlPoints,
        knots,
        weights,
        ...(command.startTangent
          ? {
              startTangent: {
                x: command.startTangent.x,
                y: command.startTangent.y,
                z: command.startTangent.z ?? 0,
              },
            }
          : {}),
        ...(command.endTangent
          ? {
              endTangent: {
                x: command.endTangent.x,
                y: command.endTangent.y,
                z: command.endTangent.z ?? 0,
              },
            }
          : {}),
        knotTolerance: 1e-7,
        controlPointTolerance: 1e-10,
        fitTolerance: 1e-10,
        layer: command.layer ?? "0",
        color: command.color ?? { kind: "byLayer" },
      });
    }

    case "geometry/create-lwpolyline": {
      const isRectGeom =
        command.closed &&
        command.vertices.length === 4 &&
        command.vertices.every((v) => (v.bulge ?? 0) === 0);
      return appendAndBuildMutation(geometryDocument, {
        id: nextGeometryEntityId(
          geometryDocument,
          isRectGeom ? "rect" : "pline",
        ),
        type: "LWPOLYLINE",
        spaceId: command.spaceId,
        vertices: command.vertices.map((v) => ({
          x: v.x,
          y: v.y,
          bulge: v.bulge ?? 0,
          startWidth: v.startWidth ?? 0,
          endWidth: v.endWidth ?? 0,
        })),
        closed: command.closed,
        layer: command.layer ?? "0",
        color: command.color ?? { kind: "byLayer" },
      });
    }

    // DONUT — encoded as a closed LWPOLYLINE with two bulge=1 vertices
    // (two semicircles forming a circle on the midline radius) and
    // constant per-segment width = (outerD - innerD) / 2. AutoCAD
    // models DONUT exactly this way: open the AutoCAD Object Browser
    // on a donut and you'll see DXF type LWPOLYLINE with bulge=1 and
    // group code 40/41 carrying half the band thickness.
    //   - midline radius = (innerD + outerD) / 4
    //   - pen width      = (outerD - innerD) / 2
    //   - innerD = 0     → solid disk (still valid)
    case "geometry/create-donut": {
      const innerR = command.innerDiameter / 2;
      const outerR = command.outerDiameter / 2;
      const midRadius = (innerR + outerR) / 2;
      const width = outerR - innerR;
      const cx = command.center.x;
      const cy = command.center.y;
      return appendAndBuildMutation(geometryDocument, {
        id: nextGeometryEntityId(geometryDocument, "donut"),
        type: "LWPOLYLINE",
        spaceId: command.spaceId,
        vertices: [
          {
            x: cx - midRadius,
            y: cy,
            bulge: 1,
            startWidth: width,
            endWidth: width,
          },
          {
            x: cx + midRadius,
            y: cy,
            bulge: 1,
            startWidth: width,
            endWidth: width,
          },
        ],
        closed: true,
        // CECOLOR — inherit the current colour stamped at the dispatch boundary
        // (default ByLayer when the command omits it — legacy / tests).
        layer: command.layer ?? "0",
        color: command.color ?? { kind: "byLayer" },
      });
    }

    case "geometry/create-text": {
      const textStyle =
        geometryDocument.styleTables.textStyles.find(
          (s) => s.name === command.styleName,
        ) ?? geometryDocument.styleTables.textStyles[0];
      const resolvedHeight =
        command.height > 0
          ? command.height
          : textStyle?.height && textStyle.height > 0
            ? textStyle.height
            : 2.5;

      return appendAndBuildMutation(geometryDocument, {
        id: nextGeometryEntityId(geometryDocument, "text"),
        type: "Text",
        spaceId: command.spaceId,
        insertionPoint: {
          x: command.insertionPoint.x,
          y: command.insertionPoint.y,
        },
        height: resolvedHeight,
        contents: command.contents,
        rotation: command.rotation,
        styleName: textStyle?.name ?? command.styleName,
        // CECOLOR — AutoCAD stamps the current colour onto a newly-created TEXT;
        // the text style colour is a fallback only when the entity colour is
        // ByLayer (default ByLayer when the command omits it — legacy / tests).
        layer: command.layer ?? "0",
        color: command.color ?? { kind: "byLayer" },
      });
    }

    case "geometry/create-mtext": {
      const mtextStyle =
        geometryDocument.styleTables.textStyles.find(
          (s) => s.name === command.styleName,
        ) ?? geometryDocument.styleTables.textStyles[0];
      const resolvedNominalHeight =
        command.nominalHeight > 0
          ? command.nominalHeight
          : mtextStyle?.height && mtextStyle.height > 0
            ? mtextStyle.height
            : 2.5;

      return appendAndBuildMutation(geometryDocument, {
        id: nextGeometryEntityId(geometryDocument, "mtext"),
        type: "MText",
        spaceId: command.spaceId,
        insertionPoint: {
          x: command.insertionPoint.x,
          y: command.insertionPoint.y,
        },
        nominalHeight: resolvedNominalHeight,
        referenceWidth: command.referenceWidth,
        contents: command.contents,
        styleName: mtextStyle?.name ?? command.styleName,
        // CECOLOR — AutoCAD stamps the current colour onto a newly-created MTEXT
        // (style colour is the ByLayer fallback). Default ByLayer when absent.
        layer: command.layer ?? "0",
        color: command.color ?? { kind: "byLayer" },
      });
    }

    case "geometry/create-dimension": {
      // @codex P1 — resolve the dimension style from the CURRENT DIMSTYLE the
      // dispatch chokepoint stamped onto `command.styleName`
      // (`stampCurrentDimStyleOntoCreateCommand` reads
      // `defaultObjectProperties["Dimension style"]`), falling back to the first
      // table entry when unset or when the name does not resolve. Before this the
      // create path ALWAYS used `dimensionStyles[0]` (usually "Standard"), so
      // "Set Current" / `DIMSTYLE SET <name>` had no effect on new dimensions.
      // @codex P2 — resolve CASE-INSENSITIVELY (DIMSTYLE names fold, like every
      // DXF symbol-table name). An explicit `styleName` from a programmatic / AI
      // caller (the dispatch stamper deliberately leaves those untouched) may use
      // foreign casing — e.g. `arch` for an imported/user `Arch`; an exact match
      // would miss and silently fall back to `dimensionStyles[0]`, creating the
      // dimension with the WRONG style.
      const requestedStyleLowered = command.styleName?.toLowerCase();
      const dimStyle =
        geometryDocument.styleTables.dimensionStyles.find(
          (s) => s.name.toLowerCase() === requestedStyleLowered,
        ) ?? geometryDocument.styleTables.dimensionStyles[0];
      const ext1 = command.extLine1Point;
      const ext2 = command.extLine2Point;
      const dimLine = command.dimensionLinePoint;
      const dimType = command.dimensionType;
      const thirdPt = command.thirdPoint;

      let measurement: number;
      if (dimType === 1) {
        // Aligned: actual distance between extension points
        measurement = Math.hypot(ext2.x - ext1.x, ext2.y - ext1.y);
      } else if (dimType === 2) {
        // Angular (2-line): angle between two lines through vertex.
        // The vertex is the dimensionLinePoint (the user-clicked arc origin
        // hint). For 2-line angular AutoCAD computes the angle between the
        // lines defined by ext1→dimLine and ext2→dimLine? Actually the
        // historical convention is that the vertex is implicit; we use
        // dimLine as the vertex for this in-app implementation.
        const v1x = ext1.x - dimLine.x;
        const v1y = ext1.y - dimLine.y;
        const v2x = ext2.x - dimLine.x;
        const v2y = ext2.y - dimLine.y;
        const dot = v1x * v2x + v1y * v2y;
        const m1 = Math.hypot(v1x, v1y) || 1;
        const m2 = Math.hypot(v2x, v2y) || 1;
        const cos = Math.max(-1, Math.min(1, dot / (m1 * m2)));
        measurement = (Math.acos(cos) * 180) / Math.PI;
      } else if (dimType === 5) {
        // Angular 3-point: vertex is explicitly given via thirdPoint.
        // Measurement is the angle between (vertex→ext1) and (vertex→ext2).
        const vx = thirdPt?.x ?? dimLine.x;
        const vy = thirdPt?.y ?? dimLine.y;
        const v1x = ext1.x - vx;
        const v1y = ext1.y - vy;
        const v2x = ext2.x - vx;
        const v2y = ext2.y - vy;
        const dot = v1x * v2x + v1y * v2y;
        const m1 = Math.hypot(v1x, v1y) || 1;
        const m2 = Math.hypot(v2x, v2y) || 1;
        const cos = Math.max(-1, Math.min(1, dot / (m1 * m2)));
        measurement = (Math.acos(cos) * 180) / Math.PI;
      } else if (dimType === 3) {
        // Diameter: ext1 and ext2 are now opposite points on the circle
        // (Phase B fix). Measurement = distance between them.
        measurement = Math.hypot(ext2.x - ext1.x, ext2.y - ext1.y);
      } else if (dimType === 4) {
        // Radius: ext1 = arc point, dimLine = centre. Measurement = distance.
        measurement = Math.hypot(dimLine.x - ext1.x, dimLine.y - ext1.y);
      } else if (dimType === 6) {
        // Ordinate: X or Y distance from origin; use the larger delta to decide axis
        const dx = Math.abs(ext2.x - ext1.x);
        const dy = Math.abs(ext2.y - ext1.y);
        measurement = dx >= dy ? Math.abs(ext1.x) : Math.abs(ext1.y);
      } else {
        // Linear (type 0): straight-line distance between extension points
        measurement = Math.hypot(ext2.x - ext1.x, ext2.y - ext1.y);
      }

      return appendAndBuildMutation(geometryDocument, {
        id: nextGeometryEntityId(geometryDocument, "dimension"),
        type: "Dimension",
        spaceId: command.spaceId,
        dimensionType: dimType,
        // DXF code 10/20/30 — definition point of the dimension line.
        // This is the point the user clicked when placing the dimension
        // line (NOT ext2). Storing ext2 here used to be a latent bug:
        // when the shell preview was rebuilt from the geometry document
        // (e.g. after a style update) the renderer's "Dimension line X/Y"
        // collapsed onto the object's edge.
        defPoint: { x: dimLine.x, y: dimLine.y },
        textMidpoint: { x: (ext1.x + ext2.x) / 2, y: dimLine.y },
        dimensionText: command.dimensionText,
        measurement,
        extLine1Point: { x: ext1.x, y: ext1.y },
        extLine2Point: { x: ext2.x, y: ext2.y },
        // Persist the explicit vertex for 3-point angular dimensions
        // (DXF group code 15/25/35).
        ...(thirdPt
          ? {
              thirdPoint: {
                x: thirdPt.x,
                y: thirdPt.y,
                z: thirdPt.z ?? 0,
              },
            }
          : {}),
        styleName: dimStyle?.name ?? "Standard",
        // textHeight = 0 means "use the dimension style's DIMTXT" (mirrors
        // AutoCAD). Pinning the value at create time would freeze the
        // text size against later style edits. The renderer falls through
        // to style.dimtxt when this is 0.
        textHeight: 0,
        // CECOLOR — AutoCAD stamps the current colour onto a newly-created
        // DIMENSION (all variants route through this one command; the dimension
        // style colour is the ByLayer fallback). Default ByLayer when absent.
        layer: command.layer ?? "0",
        color: command.color ?? { kind: "byLayer" },
      });
    }

    case "geometry/create-multileader": {
      const mleaderStyle = geometryDocument.styleTables.multileaderStyles[0];

      return appendAndBuildMutation(geometryDocument, {
        id: nextGeometryEntityId(geometryDocument, "mleader"),
        type: "Multileader",
        spaceId: command.spaceId,
        textContents: command.textContents,
        textLocation: { x: command.landingPoint.x, y: command.landingPoint.y },
        styleName: mleaderStyle?.name ?? "Standard",
        textHeight: mleaderStyle?.textHeight ?? 2.5,
        contentType: mleaderStyle?.contentType ?? 2,
        leaderRoots: [
          {
            nodes: [
              {
                lines: [
                  {
                    vertices: [
                      { x: command.arrowPoint.x, y: command.arrowPoint.y },
                    ],
                    breakPoints: [],
                    arrowheadBlock: "",
                  },
                ],
              },
            ],
          },
        ],
        // CECOLOR — AutoCAD stamps the current colour onto a newly-created
        // MULTILEADER (the multileader style colour is the ByLayer fallback).
        // Default ByLayer when absent.
        layer: command.layer ?? "0",
        color: command.color ?? { kind: "byLayer" },
      });
    }

    case "geometry/add-leader-to-multileader": {
      // MLEADEREDIT — Add Leader. Append a new `MLeaderLine` with a
      // single vertex (the clicked arrow point) to the first leader
      // root's first node. If the multileader has no roots yet (schema
      // guarantees min=1 but guard anyway), synthesize a root with one
      // node containing the new line.
      const nextSpaces = geometryDocument.spaces.map((space) => {
        let changed = false;
        const nextEntities = space.entities.map((entity) => {
          if (entity.id !== command.entityId) return entity;
          if (entity.type !== "Multileader") return entity;
          changed = true;
          const newLine = {
            vertices: [{ x: command.arrowPoint.x, y: command.arrowPoint.y }],
            breakPoints: [],
            arrowheadBlock: "",
          };
          const existingRoots = entity.leaderRoots ?? [];
          if (existingRoots.length === 0) {
            return {
              ...entity,
              leaderRoots: [
                {
                  nodes: [{ lines: [newLine] }],
                },
              ],
            };
          }
          const firstRoot = existingRoots[0];
          const firstNode = firstRoot.nodes[0] ?? { lines: [] };
          const nextFirstNode = {
            ...firstNode,
            lines: [...(firstNode.lines ?? []), newLine],
          };
          const nextFirstRoot = {
            ...firstRoot,
            nodes: [nextFirstNode, ...firstRoot.nodes.slice(1)],
          };
          return {
            ...entity,
            leaderRoots: [nextFirstRoot, ...existingRoots.slice(1)],
          };
        });
        return changed ? { ...space, entities: nextEntities } : space;
      });
      // T6c incremental validation: add-leader is in-place — it appends a
      // leader LINE to the single matched Multileader's own `leaderRoots`
      // array; it adds/removes NO entity and changes NO id/spaceId/space SET →
      // invariants preserved by construction → fast-path with the command's own
      // entityId. (Its sibling `remove-leader-from-multileader` CAN delete the
      // entity outright on last-leader removal → SET-CHANGING → stays on the
      // full assembler below.)
      return wrapHelperResult(
        geometryDocument,
        assembleDrawingDocumentInPlace(
          {
            ...geometryDocument,
            spaces: nextSpaces,
          },
          [command.entityId],
        ),
      );
    }

    case "geometry/remove-leader-from-multileader": {
      // MLEADEREDIT — Remove Leader. Remove the leader line at the
      // flat `leaderIndex` across all leaderRoots → nodes → lines.
      // Empty nodes and empty roots are also removed to keep the
      // hierarchy tidy. When the last leader is removed AND
      // `confirmed === true`, the whole entity is deleted. If the last
      // leader is removed WITHOUT `confirmed`, the document is left
      // untouched (the UI layer must re-dispatch with confirmed=true
      // after asking the user). Invalid indices are silently ignored.
      const nextSpaces = geometryDocument.spaces.map((space) => {
        let changed = false;
        const nextEntities: DrawingEntity[] = [];
        for (const entity of space.entities) {
          if (entity.id !== command.entityId || entity.type !== "Multileader") {
            nextEntities.push(entity);
            continue;
          }
          // Flatten all leader lines with (rootIdx, nodeIdx, lineIdx)
          // tuples so we can map `leaderIndex` to the exact slot.
          type Loc = {
            rootIdx: number;
            nodeIdx: number;
            lineIdx: number;
          };
          const locations: Loc[] = [];
          entity.leaderRoots.forEach((root, rootIdx) => {
            root.nodes.forEach((node, nodeIdx) => {
              node.lines.forEach((_line, lineIdx) => {
                locations.push({ rootIdx, nodeIdx, lineIdx });
              });
            });
          });
          const totalLines = locations.length;
          if (command.leaderIndex < 0 || command.leaderIndex >= totalLines) {
            // Invalid index — no-op.
            nextEntities.push(entity);
            continue;
          }
          if (totalLines === 1) {
            // Last leader — require explicit confirmation before the
            // entity is deleted outright.
            if (!command.confirmed) {
              nextEntities.push(entity);
              continue;
            }
            changed = true;
            // Drop the entity by skipping the push.
            continue;
          }
          const target = locations[command.leaderIndex];
          const nextRoots = entity.leaderRoots
            .map((root, rootIdx) => {
              if (rootIdx !== target.rootIdx) return root;
              const nextNodes = root.nodes
                .map((node, nodeIdx) => {
                  if (nodeIdx !== target.nodeIdx) return node;
                  const nextLines = node.lines.filter(
                    (_line, lineIdx) => lineIdx !== target.lineIdx,
                  );
                  return { ...node, lines: nextLines };
                })
                .filter((node) => node.lines.length > 0);
              return { ...root, nodes: nextNodes };
            })
            .filter((root) => root.nodes.length > 0);
          // If removing the line left the entity with zero leaders,
          // fall back to the "last leader" confirmation rule.
          if (nextRoots.length === 0) {
            if (!command.confirmed) {
              nextEntities.push(entity);
              continue;
            }
            changed = true;
            continue;
          }
          changed = true;
          nextEntities.push({
            ...entity,
            leaderRoots: nextRoots,
          });
        }
        return changed ? { ...space, entities: nextEntities } : space;
      });
      // T6c: LEFT on the full assembler — removing the LAST leader (confirmed)
      // DELETES the whole Multileader entity (the `continue` skips the push),
      // a SET-CHANGING edit. The full path re-validates cross-entity
      // invariants after the removal.
      return wrapHelperResult(
        geometryDocument,
        assembleDrawingDocument({
          ...geometryDocument,
          spaces: nextSpaces,
        }),
      );
    }

    // ── Phase 9 — Grip-edit reducer cases ─────────────────────
    // Each of the multileader-edit cases below mutates exactly one slice
    // of a SINGLE existing Multileader entity (matched by command.entityId)
    // and runs the result through the assembler so any structural breakage
    // surfaces immediately. Indices are validated up-front: an
    // out-of-range tuple silently no-ops (the dispatcher emits a
    // warning audit event when this happens).
    //
    // T6c incremental validation: every case here is in-place — it adds /
    // removes NO entity (only the matched entity's own leaderRoots / vertices
    // arrays change) and changes NO id / spaceId (both carried via `...entity`)
    // and touches NO space SET (unchanged spaces pass by reference). So the
    // cross-entity invariants are preserved by construction → route to the
    // fast-path assembler with the command's own entityId. `wrapHelperResult`'s
    // diff is unaffected: in dev/test the fast-path re-parses ONLY the changed
    // entity via the same `drawingEntitySchema`, yielding a byte-identical
    // `next` document to the full parse.
    case "geometry/update-multileader-vertex": {
      const nextSpaces = geometryDocument.spaces.map((space) => {
        let changed = false;
        const nextEntities = space.entities.map((entity) => {
          if (entity.id !== command.entityId) return entity;
          if (entity.type !== "Multileader") return entity;
          const root = entity.leaderRoots[command.rootIndex];
          if (!root) return entity;
          const node = root.nodes[command.nodeIndex];
          if (!node) return entity;
          const line = node.lines[command.lineIndex];
          if (!line) return entity;
          if (
            command.vertexIndex < 0 ||
            command.vertexIndex >= line.vertices.length
          )
            return entity;
          changed = true;
          const nextVertices = line.vertices.map((v, vi) =>
            vi === command.vertexIndex
              ? { x: command.newPosition.x, y: command.newPosition.y }
              : v,
          );
          const nextLine = { ...line, vertices: nextVertices };
          const nextLines = node.lines.map((ln, li) =>
            li === command.lineIndex ? nextLine : ln,
          );
          const nextNode = { ...node, lines: nextLines };
          const nextNodes = root.nodes.map((nd, ni) =>
            ni === command.nodeIndex ? nextNode : nd,
          );
          const nextRoot = { ...root, nodes: nextNodes };
          const nextRoots = entity.leaderRoots.map((rt, ri) =>
            ri === command.rootIndex ? nextRoot : rt,
          );
          return { ...entity, leaderRoots: nextRoots };
        });
        return changed ? { ...space, entities: nextEntities } : space;
      });
      return wrapHelperResult(
        geometryDocument,
        assembleDrawingDocumentInPlace(
          {
            ...geometryDocument,
            spaces: nextSpaces,
          },
          [command.entityId],
        ),
      );
    }

    case "geometry/update-multileader-text-location": {
      const nextSpaces = geometryDocument.spaces.map((space) => {
        let changed = false;
        const nextEntities = space.entities.map((entity) => {
          if (entity.id !== command.entityId) return entity;
          if (entity.type !== "Multileader") return entity;
          changed = true;
          return {
            ...entity,
            textLocation: {
              x: command.newLocation.x,
              y: command.newLocation.y,
            },
          };
        });
        return changed ? { ...space, entities: nextEntities } : space;
      });
      return wrapHelperResult(
        geometryDocument,
        assembleDrawingDocumentInPlace(
          {
            ...geometryDocument,
            spaces: nextSpaces,
          },
          [command.entityId],
        ),
      );
    }

    case "geometry/update-multileader-text-rotation": {
      const nextSpaces = geometryDocument.spaces.map((space) => {
        let changed = false;
        const nextEntities = space.entities.map((entity) => {
          if (entity.id !== command.entityId) return entity;
          if (entity.type !== "Multileader") return entity;
          changed = true;
          return { ...entity, textRotation: command.newRotation };
        });
        return changed ? { ...space, entities: nextEntities } : space;
      });
      return wrapHelperResult(
        geometryDocument,
        assembleDrawingDocumentInPlace(
          {
            ...geometryDocument,
            spaces: nextSpaces,
          },
          [command.entityId],
        ),
      );
    }

    case "geometry/update-multileader-dogleg": {
      const nextSpaces = geometryDocument.spaces.map((space) => {
        let changed = false;
        const nextEntities = space.entities.map((entity) => {
          if (entity.id !== command.entityId) return entity;
          if (entity.type !== "Multileader") return entity;
          const root = entity.leaderRoots[command.rootIndex];
          if (!root) return entity;
          const node = root.nodes[command.nodeIndex];
          if (!node) return entity;
          changed = true;
          const nextNode = {
            ...node,
            doglegVector: {
              x: command.doglegVector.x,
              y: command.doglegVector.y,
              z: command.doglegVector.z ?? 0,
            },
            doglegLength: command.doglegLength,
          };
          const nextNodes = root.nodes.map((nd, ni) =>
            ni === command.nodeIndex ? nextNode : nd,
          );
          const nextRoot = { ...root, nodes: nextNodes };
          const nextRoots = entity.leaderRoots.map((rt, ri) =>
            ri === command.rootIndex ? nextRoot : rt,
          );
          // Mirror the per-node update onto the entity-level
          // doglegLength so the renderer (which reads
          // MLENT_DOGLEG_LENGTH) updates without a separate
          // round-trip.
          return {
            ...entity,
            leaderRoots: nextRoots,
            doglegLength: command.doglegLength,
          };
        });
        return changed ? { ...space, entities: nextEntities } : space;
      });
      return wrapHelperResult(
        geometryDocument,
        assembleDrawingDocumentInPlace(
          {
            ...geometryDocument,
            spaces: nextSpaces,
          },
          [command.entityId],
        ),
      );
    }

    case "geometry/remove-multileader-vertex": {
      // Remove a single vertex from a leader line. The reducer
      // refuses to leave a leader line with fewer than 2 vertices
      // (which would be visually invisible) — in that case the
      // command silently no-ops.
      const nextSpaces = geometryDocument.spaces.map((space) => {
        let changed = false;
        const nextEntities = space.entities.map((entity) => {
          if (entity.id !== command.entityId) return entity;
          if (entity.type !== "Multileader") return entity;
          const root = entity.leaderRoots[command.rootIndex];
          if (!root) return entity;
          const node = root.nodes[command.nodeIndex];
          if (!node) return entity;
          const line = node.lines[command.lineIndex];
          if (!line) return entity;
          if (
            command.vertexIndex < 0 ||
            command.vertexIndex >= line.vertices.length
          )
            return entity;
          if (line.vertices.length <= 2) return entity;
          changed = true;
          const nextVertices = line.vertices.filter(
            (_v, vi) => vi !== command.vertexIndex,
          );
          const nextLine = { ...line, vertices: nextVertices };
          const nextLines = node.lines.map((ln, li) =>
            li === command.lineIndex ? nextLine : ln,
          );
          const nextNode = { ...node, lines: nextLines };
          const nextNodes = root.nodes.map((nd, ni) =>
            ni === command.nodeIndex ? nextNode : nd,
          );
          const nextRoot = { ...root, nodes: nextNodes };
          const nextRoots = entity.leaderRoots.map((rt, ri) =>
            ri === command.rootIndex ? nextRoot : rt,
          );
          return { ...entity, leaderRoots: nextRoots };
        });
        return changed ? { ...space, entities: nextEntities } : space;
      });
      return wrapHelperResult(
        geometryDocument,
        assembleDrawingDocumentInPlace(
          {
            ...geometryDocument,
            spaces: nextSpaces,
          },
          [command.entityId],
        ),
      );
    }

    case "geometry/add-multileader-vertex": {
      // B18 — Insert a vertex AFTER `vertexIndex` at `newPosition`.
      // Out-of-range tuples silently no-op (mirrors the rest of the
      // multileader-edit family).
      const nextSpaces = geometryDocument.spaces.map((space) => {
        let changed = false;
        const nextEntities = space.entities.map((entity) => {
          if (entity.id !== command.entityId) return entity;
          if (entity.type !== "Multileader") return entity;
          const root = entity.leaderRoots[command.rootIndex];
          if (!root) return entity;
          const node = root.nodes[command.nodeIndex];
          if (!node) return entity;
          const line = node.lines[command.lineIndex];
          if (!line) return entity;
          if (
            command.vertexIndex < 0 ||
            command.vertexIndex >= line.vertices.length
          )
            return entity;
          changed = true;
          const insertAt = command.vertexIndex + 1;
          const nextVertices = [
            ...line.vertices.slice(0, insertAt),
            { x: command.newPosition.x, y: command.newPosition.y },
            ...line.vertices.slice(insertAt),
          ];
          const nextLine = { ...line, vertices: nextVertices };
          const nextLines = node.lines.map((ln, li) =>
            li === command.lineIndex ? nextLine : ln,
          );
          const nextNode = { ...node, lines: nextLines };
          const nextNodes = root.nodes.map((nd, ni) =>
            ni === command.nodeIndex ? nextNode : nd,
          );
          const nextRoot = { ...root, nodes: nextNodes };
          const nextRoots = entity.leaderRoots.map((rt, ri) =>
            ri === command.rootIndex ? nextRoot : rt,
          );
          return { ...entity, leaderRoots: nextRoots };
        });
        return changed ? { ...space, entities: nextEntities } : space;
      });
      return wrapHelperResult(
        geometryDocument,
        assembleDrawingDocumentInPlace(
          {
            ...geometryDocument,
            spaces: nextSpaces,
          },
          [command.entityId],
        ),
      );
    }

    case "geometry/create-revcloud": {
      // REVCLOUD-MODES — AutoCAD creation methods (Autodesk REVCLOUD
      // reference: Rectangular / Polygonal / Freehand / Object). The
      // contract refine() guarantees a coherent payload for parsed
      // commands; unparsed dispatches fall back to defensive no-ops
      // (matching the create-hatch no-boundary path).
      const rcMode = command.creationMode ?? "Rectangular";
      const rcStyle = command.revisionCloudStyle ?? "Normal";
      const toCloudVertices = (pts: ReadonlyArray<{ x: number; y: number }>) =>
        pts.map((p) => ({
          x: p.x,
          y: p.y,
          bulge: 0.5,
          startWidth: 0,
          endWidth: 0,
        }));

      if (rcMode === "Object") {
        // Convert an existing CLOSED entity (circle / ellipse / closed
        // polyline / closed spline) into a revision cloud. AutoCAD
        // REPLACES the source object by default and honours the
        // "Reverse direction [Yes/No]" prompt; the cloud lands in the
        // SOURCE entity's space (the conversion happens in place).
        //
        // ONE validity truth with the shell arm (geometryShellReducer
        // create-revcloud): both reducers resolve the source through
        // `resolveRevcloudObjectSource`, so the shell can never append a
        // preview row for a command this arm no-ops (missing / unknown /
        // non-closed source — AutoCAD's "The selected object is not a
        // valid closed object" rejection).
        const rcResolved = resolveRevcloudObjectSource(
          geometryDocument.spaces,
          command.sourceEntityId,
          command.arcChordLength,
        );
        if (!rcResolved) {
          return { document: geometryDocument, mutations: NO_MUTATIONS };
        }
        const rcChain = command.reverseDirection
          ? [...rcResolved.outline].reverse()
          : rcResolved.outline;
        // Mint the cloud id from the PRE-removal document: minting from the
        // source-stripped doc could re-mint the source's OWN id (an imported
        // entity literally named "revcloud-1"), which would collapse the
        // removed+added mutation pair into an "updated" diff and break the
        // HIST-REVCLOUD-INVERSE undo. `nextGeometryEntityId` skips every id
        // present in the document it is given, so including the source
        // guarantees mintedId !== sourceEntityId.
        const rcCloudId = nextGeometryEntityId(geometryDocument, "revcloud");
        const rcWithoutSource = removeGeometryEntitiesById(geometryDocument, [
          rcResolved.source.id,
        ]);
        const rcNext = appendGeometryEntity(rcWithoutSource, {
          id: rcCloudId,
          type: "Revision Cloud",
          spaceId: rcResolved.spaceId,
          vertices: toCloudVertices(rcChain),
          closed: true,
          arcChordLength: command.arcChordLength,
          approximateArcLength: command.arcChordLength,
          revisionCloudStyle: rcStyle,
          creationMode: "Object",
          // CECOLOR — inherit the current colour stamped at the dispatch boundary.
          layer: command.layer ?? "0",
          color: command.color ?? { kind: "byLayer" },
        });
        // Diff-based mutations: removed = source, added = cloud — feeds
        // both the cache commit and the HIST-REVCLOUD-INVERSE
        // delete-cloud + restore-source inverse in computeInverse.
        return wrapHelperResult(geometryDocument, rcNext);
      }

      if (rcMode === "Polygonal" || rcMode === "Freehand") {
        // Polygonal: arcs along each picked polygon segment. Freehand:
        // arcs along the sampled cursor path — persisted as a Polygonal
        // vertex chain (the audited RevcloudProps XData mode enum has no
        // Freehand code; see contracts/src/editor.ts).
        const rcVertices = command.vertices ?? [];
        if (rcVertices.length < 3) {
          return { document: geometryDocument, mutations: NO_MUTATIONS };
        }
        return appendAndBuildMutation(geometryDocument, {
          id: nextGeometryEntityId(geometryDocument, "revcloud"),
          type: "Revision Cloud",
          spaceId: command.spaceId,
          vertices: toCloudVertices(rcVertices),
          closed: true,
          arcChordLength: command.arcChordLength,
          approximateArcLength: command.arcChordLength,
          revisionCloudStyle: rcStyle,
          creationMode: "Polygonal",
          // CECOLOR — inherit the current colour stamped at the dispatch boundary.
          layer: command.layer ?? "0",
          color: command.color ?? { kind: "byLayer" },
        });
      }

      // Rectangular (default / legacy two-corner flow).
      const c1 = command.corner1;
      const c2 = command.corner2;
      if (!c1 || !c2) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }
      const revcloudVertices = [
        { x: c1.x, y: c1.y, bulge: 0.5, startWidth: 0, endWidth: 0 },
        { x: c2.x, y: c1.y, bulge: 0.5, startWidth: 0, endWidth: 0 },
        { x: c2.x, y: c2.y, bulge: 0.5, startWidth: 0, endWidth: 0 },
        { x: c1.x, y: c2.y, bulge: 0.5, startWidth: 0, endWidth: 0 },
      ];

      return appendAndBuildMutation(geometryDocument, {
        id: nextGeometryEntityId(geometryDocument, "revcloud"),
        type: "Revision Cloud",
        spaceId: command.spaceId,
        vertices: revcloudVertices,
        closed: true,
        arcChordLength: command.arcChordLength,
        approximateArcLength: command.arcChordLength,
        revisionCloudStyle: rcStyle,
        creationMode: "Rectangular",
        // CECOLOR — inherit the current colour stamped at the dispatch boundary.
        layer: command.layer ?? "0",
        color: command.color ?? { kind: "byLayer" },
      });
    }

    case "geometry/create-wipeout": {
      // WIPEOUT — closed polygon with opaque background fill that
      // hides entities rendered beneath it. The state machine sends
      // a pre-computed vertices array (rectangular mode expands the
      // two corners into 4 vertices in the draft state machine; the
      // future polygon-pick mode will collect vertices directly).
      // Frame visibility defaults to true (matches AutoCAD's
      // WIPEOUTFRAME = 1 default).
      return appendAndBuildMutation(geometryDocument, {
        id: nextGeometryEntityId(geometryDocument, "wipeout"),
        type: "Wipeout",
        spaceId: command.spaceId,
        vertices: command.vertices.map((v) => ({ x: v.x, y: v.y })),
        frame: command.frame,
        // CECOLOR — inherit the current colour stamped at the dispatch boundary.
        layer: command.layer ?? "0",
        color: command.color ?? { kind: "byLayer" },
      });
    }

    case "geometry/create-hatch": {
      // Two-mode authoring:
      //   1. Internal-point pick (Phase E) — `boundaryPaths` carries
      //      the loops produced by `findBoundaryFromSeed` and the
      //      seed clicks themselves are stored on the entity for
      //      round-trip fidelity (HATCH XDATA).
      //   2. Rectangular corner-pick (legacy) — two opposite corners
      //      are expanded into a 4-edge rectangular loop.
      const seedPoints =
        command.seedPoints?.map((p) => ({ x: p.x, y: p.y })) ?? [];
      let boundaryPaths;
      if (command.boundaryPaths && command.boundaryPaths.length > 0) {
        boundaryPaths = command.boundaryPaths.map((path) => ({
          pathTypeFlag: path.pathTypeFlag,
          edges: path.edges.map((edge) => {
            if (edge.edgeType === "line") {
              return {
                edgeType: "line" as const,
                start: { x: edge.start.x, y: edge.start.y },
                end: { x: edge.end.x, y: edge.end.y },
              };
            }
            return {
              edgeType: "arc" as const,
              center: { x: edge.center.x, y: edge.center.y },
              radius: edge.radius,
              startAngle: edge.startAngle,
              endAngle: edge.endAngle,
              counterclockwise: edge.counterclockwise,
            };
          }),
        }));
      } else if (command.boundaryCorner1 && command.boundaryCorner2) {
        const bc1 = command.boundaryCorner1;
        const bc2 = command.boundaryCorner2;
        boundaryPaths = [
          {
            pathTypeFlag: 1,
            edges: [
              {
                edgeType: "line" as const,
                start: { x: bc1.x, y: bc1.y },
                end: { x: bc2.x, y: bc1.y },
              },
              {
                edgeType: "line" as const,
                start: { x: bc2.x, y: bc1.y },
                end: { x: bc2.x, y: bc2.y },
              },
              {
                edgeType: "line" as const,
                start: { x: bc2.x, y: bc2.y },
                end: { x: bc1.x, y: bc2.y },
              },
              {
                edgeType: "line" as const,
                start: { x: bc1.x, y: bc2.y },
                end: { x: bc1.x, y: bc1.y },
              },
            ],
          },
        ];
      } else {
        // Defensive: schema requires either corners or boundaryPaths.
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }

      return appendAndBuildMutation(geometryDocument, {
        id: nextGeometryEntityId(geometryDocument, "hatch"),
        type: "Hatch",
        spaceId: command.spaceId,
        patternName: command.patternName,
        patternScale: command.patternScale,
        patternAngle: command.patternAngle,
        boundaryPaths,
        seedPoints,
        // CECOLOR — inherit the current colour stamped at the dispatch boundary
        // (a new HATCH inherits CECOLOR in AutoCAD).
        layer: command.layer ?? "0",
        color: command.color ?? { kind: "byLayer" },
      });
    }

    case "geometry/create-block-insert": {
      // Entity construction lives in `buildBlockInsertEntityInput`
      // (geometryReducerHelpers.ts) — the SINGLE source of truth shared with
      // the T6b batch sibling below, so the two paths can never drift.
      return appendAndBuildMutation(
        geometryDocument,
        buildBlockInsertEntityInput(
          geometryDocument,
          nextGeometryEntityId(geometryDocument, "block"),
          command.spaceId,
          command.blockName,
          command.color,
          {
            insertionPoint: command.insertionPoint,
            scaleX: command.scaleX,
            scaleY: command.scaleY,
            rotation: command.rotation,
          },
          // T7 (agent-bridge) local Codex round-18 P2 (post-T6b-merge
          // regression catch) — CLAYER stamped at the dispatch boundary,
          // mirroring the `command.color` argument directly above.
          command.layer,
        ),
      );
    }

    // T6b — AI-dispatch coalescing. Batches K `geometry/create-block-insert`
    // copies into ONE reducer pass: mint every id from a SINGLE once-seeded
    // `createEntityIdAllocator` scan (amortized O(1) per mint, replicating
    // `nextGeometryEntityId`'s exact id sequence — see its JSDoc) and append
    // the whole run with ONE `appendGeometryEntities` → ONE
    // `assembleDrawingDocument` validate pass, instead of K
    // (nextGeometryEntityId + assembleDrawingDocument) pairs. Mirrors T6a's
    // `handleCreateArrayRect` pattern (arrayReducers.ts), applied to the AI
    // bridge-dispatch path (`aiSafeContour.ts`'s `dispatchAiCommandSequence`,
    // the exclusive synthesizer of this command — see
    // `coverage-allowlist.ts` section 11). Byte-identical per-entity output to
    // dispatching `command.inserts.length` individual `create-block-insert`
    // commands in order: same `buildBlockInsertEntityInput` builder, same
    // spaceId/blockName/color/layer shared across every copy, same
    // drop-unknown-space no-op semantics (all-or-nothing for the whole batch,
    // since every copy targets the SAME spaceId).
    case "geometry/create-block-inserts-batch": {
      const idAllocator = createEntityIdAllocator(geometryDocument);
      const entities = command.inserts.map((copy) =>
        buildBlockInsertEntityInput(
          geometryDocument,
          idAllocator.next("block"),
          command.spaceId,
          command.blockName,
          command.color,
          copy,
          // T7 (agent-bridge) local Codex round-18 P2 — CLAYER stamped at the
          // dispatch boundary, mirroring `command.color` directly above.
          command.layer,
        ),
      );
      const next = appendGeometryEntities(geometryDocument, entities);
      return wrapHelperResult(geometryDocument, next);
    }

    // ── BLOCK: full definition authoring pipeline ──────────────────
    // The App-level handleBlockDefinitionConfirm collects selected
    // entities, clones them into blockDef.entities, and dispatches this
    // command. The heavy lifting (schema validation, name validation via
    // createBlockDefinitionMutation, postAction bookkeeping, optional
    // convert-insert creation) lives in blockReducerHelpers so this file
    // stays under the CLAUDE.md 2000-line soft limit.
    case "geometry/create-block-definition": {
      return wrapHelperResult(
        geometryDocument,
        createBlockDefinitionInDocument(geometryDocument, {
          blockDef:
            command.blockDef as CreateBlockDefinitionCommandInput["blockDef"],
          blockRecord:
            command.blockRecord as CreateBlockDefinitionCommandInput["blockRecord"],
          postAction: command.postAction,
          sourceEntityIds: command.sourceEntityIds,
          ...(command.convertSpaceId !== undefined
            ? { convertSpaceId: command.convertSpaceId }
            : {}),
          ...(command.convertInsertPoint !== undefined
            ? { convertInsertPoint: command.convertInsertPoint }
            : {}),
          ...(command.convertInsertId !== undefined
            ? { convertInsertId: command.convertInsertId }
            : {}),
          // CECOLOR round-6 FINDING C — thread the INSERT-only convert colour
          // (stamped at the dispatch chokepoint) into the helper so the
          // postAction=convert replacement INSERT inherits CECOLOR. The block
          // definition itself stays colour-less.
          ...(command.convertInsertColor !== undefined
            ? { convertInsertColor: command.convertInsertColor }
            : {}),
          // T7 (agent-bridge) local Codex round-17 P2 — the CLAYER sibling of
          // convertInsertColor directly above: thread the INSERT-only convert
          // layer (stamped at the dispatch chokepoint) into the helper.
          ...(command.convertInsertLayer !== undefined
            ? { convertInsertLayer: command.convertInsertLayer }
            : {}),
        }),
      );
    }

    // ── BLOCK definition removal (history-internal undo inverse) ───
    // The undo inverse of geometry/create-block-definition (see
    // computeInverse). Removes the BlockDefinition + BlockRecord the forward
    // registered under `command.name`. Unconditional by design: the undo batch
    // replays the INSERT inverses before this command, so the block is already
    // unreferenced when it runs. No-ops gracefully if the block is already
    // absent (defensive — keeps redo/abort idempotent). Mirrors how block defs
    // are added (createBlockDefinitionInDocument writes blocks[name] +
    // blockRecords[name]); this is the precise structural reversal.
    case "geometry/remove-block-definition": {
      const blockExists = command.name in geometryDocument.blocks;
      const recordExists = command.name in geometryDocument.blockRecords;
      if (!blockExists && !recordExists) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }
      const nextBlocks = { ...geometryDocument.blocks };
      delete nextBlocks[command.name];
      const nextBlockRecords = { ...geometryDocument.blockRecords };
      delete nextBlockRecords[command.name];
      const next = assembleDrawingDocument({
        ...geometryDocument,
        blocks: nextBlocks,
        blockRecords: nextBlockRecords,
      });
      return wrapHelperResult(geometryDocument, next);
    }

    // ── EXPLODE an INSERT entity ─────────────────────────────────
    // Replaces an INSERT with its constituent child entities. Mirrors the
    // math in packages/command-engine/src/blockMutations.ts explodeInsertMutation:
    // each child is transformed by composeInsertMatrix(insertion, -rotation,
    // scaleX, scaleY, basePoint). Circle/Arc under non-uniform scale promote
    // to Ellipse (requires Ellipse entity type). Visible ATTRIBs become Text
    // entities. Invisible ATTRIBs are dropped. resolveNested="one-level"
    // matches AutoCAD: nested INSERTs are preserved as INSERTs so the user
    // can re-explode them. Non-explodable blocks silently no-op.
    case "geometry/explode-insert": {
      // Locate the INSERT entity by id across all spaces.
      let insertEntity: DrawingEntity | null = null;
      for (const space of geometryDocument.spaces) {
        const candidate = space.entities.find(
          (entity) => entity.id === command.insertId,
        );
        if (candidate && candidate.type === "Block") {
          insertEntity = candidate;
          break;
        }
      }
      if (!insertEntity || insertEntity.type !== "Block") {
        // Silently no-op — missing INSERT is not a fatal error.
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }

      const blockDef = geometryDocument.blocks[insertEntity.blockName];
      if (!blockDef) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }
      const blockRecord = geometryDocument.blockRecords[insertEntity.blockName];

      // explodable check — non-explodable blocks should not be exploded.
      if (blockRecord && blockRecord.explodable === false) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }

      return wrapHelperResult(
        geometryDocument,
        explodeBlockInsertInDocument(geometryDocument, insertEntity, blockDef),
      );
    }

    // ── PURGE unreferenced blocks (Phase 2.7) ──────────────────
    // Remove the user-confirmed list of block names from blocks[] and
    // blockRecords[]. applyPurgeBlocks re-analyses candidates defensively
    // so any name that became in-use between dialog-open and dispatch is
    // silently skipped (not an error — the UI re-reads the list on open).
    case "file/purge-blocks": {
      const result = applyPurgeBlocks(geometryDocument, command.blockNames);
      return wrapHelperResult(geometryDocument, result.updatedDocument);
    }

    // ── RENAME a block with cascade across all INSERTs (Phase 2.8) ──
    // renameBlockMutation validates old/new names, then walks every space
    // and every block definition to rewrite blockName references. Throws
    // on validation error — App.tsx wraps the dispatch in try/catch so
    // the user sees a toast and the dialog stays open.
    case "file/rename-block": {
      const result = renameBlockMutation({
        oldName: command.oldName,
        newName: command.newName,
        drawingDocument: geometryDocument,
      });
      return wrapHelperResult(geometryDocument, result.updatedDocument);
    }

    // ── XREF suite (Phase FIN.3) ─────────────────────────────────
    // XATTACH attaches a new xref. The UI parses the external DXF and
    // hands the entities through the command payload; the reducer
    // delegates to xattachMutation which adds the BLOCK_RECORD, the
    // BLOCK definition (xref-flagged), the xrefRegistry entry, and an
    // INSERT in the active space. Failures throw → App.tsx toasts.
    case "file/xattach": {
      const result = xattachMutation({
        drawingDocument: geometryDocument,
        xrefName: command.xrefName,
        xrefPath: command.xrefPath,
        xrefEntities: command.xrefEntities as BlockChildEntity[],
        attachmentType: command.attachmentType,
        insertionPoint: command.insertionPoint,
        insertSpaceId: command.insertSpaceId,
      });
      return wrapHelperResult(geometryDocument, result.updatedDocument);
    }
    case "file/xbind": {
      const result = xbindMutation({
        drawingDocument: geometryDocument,
        xrefName: command.xrefName,
      });
      return wrapHelperResult(geometryDocument, result.updatedDocument);
    }
    case "file/xunload": {
      const result = xunloadMutation({
        drawingDocument: geometryDocument,
        xrefName: command.xrefName,
      });
      return wrapHelperResult(geometryDocument, result.updatedDocument);
    }
    case "file/xreload": {
      const result = xreloadMutation({
        drawingDocument: geometryDocument,
        xrefName: command.xrefName,
        ...(command.newEntities
          ? { newEntities: command.newEntities as BlockChildEntity[] }
          : {}),
        ...(command.newPath !== undefined ? { newPath: command.newPath } : {}),
      });
      return wrapHelperResult(geometryDocument, result.updatedDocument);
    }
    case "file/xdetach": {
      const result = xdetachMutation({
        drawingDocument: geometryDocument,
        xrefName: command.xrefName,
      });
      return wrapHelperResult(geometryDocument, result.updatedDocument);
    }
    case "file/xclip": {
      const result = xclipMutation({
        drawingDocument: geometryDocument,
        xrefName: command.xrefName,
        boundary: command.boundary as XrefClipBoundary | null,
      });
      return wrapHelperResult(geometryDocument, result.updatedDocument);
    }
    // ── XREF history inverse (ABB-S1 round-3 F2/F3) ──────────────────────
    // Restore the four xref-bearing document slices captured PRE-mutation by
    // `computeInverse`. This is the undo (and, replayed, the redo) of EVERY
    // `file/x*` command — XATTACH/XBIND/XUNLOAD/XRELOAD/XDETACH/XCLIP. A
    // verbatim slice restore is exact for all six (XDETACH's cross-space INSERT
    // removal is reversed by restoring `spaces`; XBIND's flag flip + registry
    // drop by restoring `blocks`+`xrefRegistry`; etc.). Re-assembled through
    // `assembleDrawingDocument` so the restored doc is re-validated (DEV) /
    // invariant-checked (PROD), identical to every other mutation's exit path.
    case "file/xref-restore-snapshot": {
      const restored = assembleDrawingDocument({
        ...geometryDocument,
        blocks: command.blocks as DrawingDocument["blocks"],
        blockRecords: command.blockRecords as DrawingDocument["blockRecords"],
        xrefRegistry: command.xrefRegistry as DrawingDocument["xrefRegistry"],
        spaces: command.spaces as DrawingDocument["spaces"],
      });
      return wrapHelperResult(geometryDocument, restored);
    }

    // ── ATTDEF authoring (Phase 2.1) ──
    // Only valid inside an active `block-edit` session whose
    // `blockEditName` matches command.blockName. Outside of BEDIT the
    // command is a silent no-op — App.tsx surfaces the toast. All
    // validation happens inside handleCreateAttdef via
    // createAttdefMutation; thrown errors leave the document untouched.
    case "geometry/create-attdef": {
      return wrapHelperResult(
        geometryDocument,
        handleCreateAttdef(geometryDocument, command),
      );
    }

    // ── Single-tag ATTRIB update (DDATTE / ATTEDIT).
    case "geometry/update-attribute-value": {
      return wrapHelperResult(
        geometryDocument,
        handleUpdateAttributeValue(geometryDocument, command),
      );
    }

    // ── Batch ATTRIB update (EATTEDIT Apply/OK).
    case "geometry/update-attribute-values": {
      return wrapHelperResult(
        geometryDocument,
        handleUpdateAttributeValues(geometryDocument, command),
      );
    }

    // ── BATTMAN — ATTDEF removal with optional cascade (Phase 2.4).
    case "geometry/remove-attdef": {
      return wrapHelperResult(
        geometryDocument,
        handleRemoveAttdef(geometryDocument, command),
      );
    }

    // ── BATTMAN — ATTDEF property update (Phase 2.4).
    case "geometry/update-attdef": {
      return wrapHelperResult(
        geometryDocument,
        handleUpdateAttdef(geometryDocument, command),
      );
    }

    // ── BATTMAN — ATTDEF tag rename with cascade (Phase 2.4).
    case "geometry/rename-attdef-tag": {
      return wrapHelperResult(
        geometryDocument,
        handleRenameAttdefTag(geometryDocument, command),
      );
    }

    // ── BATTMAN — ATTDEF reorder (Phase 2.4).
    case "geometry/reorder-attdefs": {
      return wrapHelperResult(
        geometryDocument,
        handleReorderAttdefs(geometryDocument, command),
      );
    }

    // ── ATTSYNC — push ATTDEF template changes into existing INSERTs.
    case "geometry/attsync": {
      return wrapHelperResult(
        geometryDocument,
        handleAttsync(geometryDocument, command),
      );
    }

    // ── Dynamic block authoring (DYN-1.7 + DYN-1.8) ──
    // All guarded by BEDIT context in the helper — outside of
    // block-edit they are silent no-ops.
    case "geometry/add-dynamic-parameter": {
      return wrapHelperResult(
        geometryDocument,
        handleAddDynamicParameter(geometryDocument, command),
      );
    }
    case "geometry/update-dynamic-parameter": {
      return wrapHelperResult(
        geometryDocument,
        handleUpdateDynamicParameter(geometryDocument, command),
      );
    }
    case "geometry/remove-dynamic-parameter": {
      return wrapHelperResult(
        geometryDocument,
        handleRemoveDynamicParameter(geometryDocument, command),
      );
    }
    case "geometry/add-dynamic-action": {
      return wrapHelperResult(
        geometryDocument,
        handleAddDynamicAction(geometryDocument, command),
      );
    }
    case "geometry/update-dynamic-action": {
      return wrapHelperResult(
        geometryDocument,
        handleUpdateDynamicAction(geometryDocument, command),
      );
    }
    case "geometry/remove-dynamic-action": {
      return wrapHelperResult(
        geometryDocument,
        handleRemoveDynamicAction(geometryDocument, command),
      );
    }
    case "geometry/add-visibility-state": {
      return wrapHelperResult(
        geometryDocument,
        handleAddVisibilityState(geometryDocument, command),
      );
    }
    case "geometry/rename-visibility-state": {
      return wrapHelperResult(
        geometryDocument,
        handleRenameVisibilityState(geometryDocument, command),
      );
    }
    case "geometry/delete-visibility-state": {
      return wrapHelperResult(
        geometryDocument,
        handleDeleteVisibilityState(geometryDocument, command),
      );
    }
    case "geometry/reorder-visibility-states": {
      return wrapHelperResult(
        geometryDocument,
        handleReorderVisibilityStates(geometryDocument, command),
      );
    }
    case "geometry/set-default-visibility-state": {
      return wrapHelperResult(
        geometryDocument,
        handleSetDefaultVisibilityState(geometryDocument, command),
      );
    }
    case "geometry/toggle-visibility-state-entity": {
      return wrapHelperResult(
        geometryDocument,
        handleToggleVisibilityStateEntity(geometryDocument, command),
      );
    }
    // ── Phase K.4 — Visibility States Manager additions ───
    case "geometry/set-current-visibility-state": {
      return wrapHelperResult(
        geometryDocument,
        handleSetCurrentVisibilityState(geometryDocument, command),
      );
    }
    case "geometry/bvhide": {
      return wrapHelperResult(
        geometryDocument,
        handleBvhide(geometryDocument, command),
      );
    }
    case "geometry/bvshow": {
      return wrapHelperResult(
        geometryDocument,
        handleBvshow(geometryDocument, command),
      );
    }
    case "geometry/bvshow-all": {
      return wrapHelperResult(
        geometryDocument,
        handleBvshowAll(geometryDocument, command),
      );
    }

    // ── DYN-3 Batch C — Chain Actions (pipeline authoring) ──
    case "geometry/create-chain-group": {
      return wrapHelperResult(
        geometryDocument,
        handleCreateChainGroup(geometryDocument, command),
      );
    }
    case "geometry/add-action-to-chain-group": {
      return wrapHelperResult(
        geometryDocument,
        handleAddActionToChainGroup(geometryDocument, command),
      );
    }
    case "geometry/remove-action-from-chain": {
      return wrapHelperResult(
        geometryDocument,
        handleRemoveActionFromChain(geometryDocument, command),
      );
    }
    case "geometry/reorder-chain-group": {
      return wrapHelperResult(
        geometryDocument,
        handleReorderChainGroup(geometryDocument, command),
      );
    }

    // ── DYN-3 Batch E — Geometric constraint addition ──────
    case "geometry/add-geometric-constraint": {
      return wrapHelperResult(
        geometryDocument,
        handleAddGeometricConstraint(geometryDocument, command),
      );
    }

    // ── DYN-2 B.2 Lookup Tables + Block Properties Table ───
    case "geometry/create-lookup-table": {
      return wrapHelperResult(
        geometryDocument,
        handleCreateLookupTable(geometryDocument, command),
      );
    }
    case "geometry/update-lookup-table": {
      return wrapHelperResult(
        geometryDocument,
        handleUpdateLookupTable(geometryDocument, command),
      );
    }
    case "geometry/delete-lookup-table": {
      return wrapHelperResult(
        geometryDocument,
        handleDeleteLookupTable(geometryDocument, command),
      );
    }
    case "geometry/set-block-properties-table": {
      return wrapHelperResult(
        geometryDocument,
        handleSetBlockPropertiesTable(geometryDocument, command),
      );
    }
    case "geometry/add-block-property": {
      return wrapHelperResult(
        geometryDocument,
        handleAddBlockProperty(geometryDocument, command),
      );
    }
    case "geometry/update-block-property": {
      return wrapHelperResult(
        geometryDocument,
        handleUpdateBlockProperty(geometryDocument, command),
      );
    }
    case "geometry/remove-block-property": {
      return wrapHelperResult(
        geometryDocument,
        handleRemoveBlockProperty(geometryDocument, command),
      );
    }
    case "geometry/reorder-block-properties": {
      return wrapHelperResult(
        geometryDocument,
        handleReorderBlockProperties(geometryDocument, command),
      );
    }

    // ── Phase N.5 — Block Properties Manager (BPM) ──────────────
    // Drawing-wide parameter / action editing. Unlike the BEDIT-gated
    // authoring commands above, these handlers do NOT require an active
    // block-edit session.
    case "geometry/rename-block-parameter": {
      return wrapHelperResult(
        geometryDocument,
        handleRenameBlockParameter(geometryDocument, command),
      );
    }
    case "geometry/update-block-parameter-description": {
      return wrapHelperResult(
        geometryDocument,
        handleUpdateBlockParameterDescription(geometryDocument, command),
      );
    }
    case "geometry/toggle-parameter-show-in-palette": {
      return wrapHelperResult(
        geometryDocument,
        handleToggleParameterShowInPalette(geometryDocument, command),
      );
    }
    case "geometry/reorder-block-parameters": {
      return wrapHelperResult(
        geometryDocument,
        handleReorderBlockParameters(geometryDocument, command),
      );
    }
    case "geometry/rename-block-action": {
      return wrapHelperResult(
        geometryDocument,
        handleRenameBlockAction(geometryDocument, command),
      );
    }
    case "geometry/update-block-action-description": {
      return wrapHelperResult(
        geometryDocument,
        handleUpdateBlockActionDescription(geometryDocument, command),
      );
    }
    case "geometry/reorder-block-actions": {
      return wrapHelperResult(
        geometryDocument,
        handleReorderBlockActions(geometryDocument, command),
      );
    }

    case "geometry/create-arrayrect": {
      return wrapHelperResult(
        geometryDocument,
        handleCreateArrayRect(geometryDocument, command),
      );
    }

    case "geometry/create-arraypolar": {
      return wrapHelperResult(
        geometryDocument,
        handleCreateArrayPolar(geometryDocument, command),
      );
    }

    case "geometry/create-arraypath": {
      return wrapHelperResult(
        geometryDocument,
        handleCreateArrayPath(geometryDocument, command),
      );
    }

    case "geometry/arrayedit": {
      return wrapHelperResult(
        geometryDocument,
        handleArrayEdit(geometryDocument, command),
      );
    }

    case "geometry/hatchedit": {
      // HATCHEDIT — edit a placed HATCH entity's wired pattern fields. DEFUSE S15
      // Slice E: the COUPLED pattern fields (patternName → solidFill / gradient /
      // customPatternLines) route through the ONE `applyHatchPatternMutation` CAD
      // core the palette side-effect Step (Step 1d-se) also uses — no parallel
      // hand-rolled reconcile, no lossy preview-string label round-trip. The
      // SCALAR fields (patternScale / patternAngle / associative / hatchStyle /
      // patternType) are applied RAW from the typed command (full contract
      // precision, valid vendor 75/76 codes survive verbatim). Undo/redo + DXF
      // export carry the edit because the mutation lands via `patchEntityById`.
      //
      // Locate the target HATCH across all spaces (model + layouts).
      let docHatch: DrawingEntity | undefined;
      for (const space of geometryDocument.spaces) {
        const found = space.entities.find((e) => e.id === command.entityId);
        if (found) {
          docHatch = found;
          break;
        }
      }
      // Unknown id or wrong type → no-op (mirrors arrayedit's tolerant guards).
      if (!docHatch || docHatch.type !== "Hatch") {
        return wrapHelperResult(geometryDocument, geometryDocument);
      }
      const hatch = docHatch as HatchEntity;
      const rawPatch: Partial<HatchEntity> = {};
      // SCALARS — applied RAW (full precision, valid raw codes preserved).
      if (command.patternScale !== undefined) {
        rawPatch.patternScale = command.patternScale;
      }
      if (command.patternAngle !== undefined) {
        rawPatch.patternAngle = command.patternAngle;
      }
      if (command.associative !== undefined) {
        rawPatch.associative = command.associative;
      }
      if (command.hatchStyle !== undefined) rawPatch.hatchStyle = command.hatchStyle;
      if (command.patternType !== undefined) {
        rawPatch.patternType = command.patternType;
      }
      // N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR (@codex P2 round 6) — DXF group
      // 77, applied RAW like the other scalars above (no per-field
      // readOnly gate here — HATCHEDIT/undo restore is a privileged raw
      // typed-field write, not a `properties/apply` descriptor edit).
      if (command.doubleLine !== undefined) {
        rawPatch.doubleLine = command.doubleLine;
      }
      // COUPLED FIELDS — one truth via the mutator. CONTRACT (@codex P2-1,
      // undefined-vs-null): an OMITTED field is UNTOUCHED — a present-but-
      // `undefined` value (a typed caller spreading an optional field while
      // editing an UNRELATED field) must NOT clear the stored gradient / custom
      // lines. Only an explicit `null` is restore-to-absent; a real payload
      // restores it VERBATIM (UNDO inverse). So gate on `!== undefined` (NOT the
      // key-presence `in`), restoring the pre-slice reducer's present-but-
      // undefined-ignored behaviour. Diff the mutated entity against the source so
      // ONLY the fields that actually changed enter the patch (an unchanged
      // Pattern re-submit preserves the stored gradient + solidFill).
      const hasCoupledField =
        command.patternName !== undefined ||
        command.solidFill !== undefined ||
        command.gradient !== undefined ||
        command.customPatternLines !== undefined;
      if (hasCoupledField) {
        const mutation: HatchPatternMutation = {
          ...(command.patternName !== undefined
            ? { patternName: command.patternName }
            : {}),
          ...(command.solidFill !== undefined
            ? { solidFill: command.solidFill }
            : {}),
          ...(command.gradient !== undefined
            ? { gradient: command.gradient as HatchGradient | null }
            : {}),
          ...(command.customPatternLines !== undefined
            ? {
                customPatternLines:
                  command.customPatternLines as HatchPatternLine[] | null,
              }
            : {}),
        };
        const mutated = applyHatchPatternMutation(hatch, mutation);
        for (const key of [
          "patternName",
          "solidFill",
          "gradient",
          "customPatternLines",
        ] as const) {
          if (!Object.is(mutated[key], hatch[key])) {
            (rawPatch as Record<string, unknown>)[key] = mutated[key];
          }
        }
      }
      // Empty payload OR an all-unchanged edit → a true no-op (keeps the reducer's
      // ">=1 effective field" posture; an empty edit does nothing, not throwing).
      if (Object.keys(rawPatch).length === 0) {
        return wrapHelperResult(geometryDocument, geometryDocument);
      }
      // Single-entity in-place field patch — id/spaceId preserved, no entity
      // added/removed → invariants hold by construction → fast-path assembler
      // with this entityId (identical to the properties/apply Step 2 write).
      const nextDoc = assembleDrawingDocumentInPlace(
        patchEntityById(geometryDocument, command.entityId, rawPatch),
        [command.entityId],
      );
      return wrapHelperResult(geometryDocument, nextDoc);
    }

    case "properties/apply": {
      const nextDoc = command.entityIds.reduce((document, entityId) => {
        const previewEntity = previewEntities.find(
          (entity) => entity.id === entityId,
        );

        if (!previewEntity) {
          return document;
        }

        // Phase 4.1.e: per-entity value overrides take precedence for undo/redo inverse dispatch.
        const rawValues = command.perEntityValues?.[entityId] ?? command.values;
        // GRIPS-SUBSYSTEM (§5.11) — a grip-drag commits a `properties/apply`
        // carrying the pseudo-keys (gripDragWorldX/Y/gripId/gripKind). The
        // shell reducer translates them into the REAL geometry-property patch
        // for the live preview; the GEOMETRY document must apply the SAME
        // translated patch (one-truth `translateGripDrag`) or the edit never
        // reaches the persisted model — DXF export + undo would both miss it
        // (Codex P1: grip-drag was not undoable because the doc never changed,
        // so no history entry was recorded). Translate against the pre-mutation
        // preview (carries the current geometry), then continue as a normal
        // geometry-property apply. A grip id unrecognised for the entity type
        // yields an empty patch → no-op.
        //
        // Codex P2-5 (#317) — thread the LIVE document entity into the grip-drag
        // translator so untouched LWPOLYLINE/SPLINE points keep their EXACT
        // document precision (the preview point strings are formatNumber-rounded
        // to 3 decimals; serializing the whole list back from the preview
        // persisted rounded X/Y for every UNTOUCHED point). The pre-mutation
        // accumulator `document` is the authoritative source here.
        // Locate the PRE-mutation document entity (full precision). Used by the
        // grip-drag translator (LWPOLYLINE/SPLINE untouched-vertex precision) AND
        // by the simple-geometry LINE reconciliation below (#317 residual R2 —
        // a Properties-palette axis edit must keep the untouched X/Y/Z at raw
        // document precision, not the 3-decimal rounded preview). Resolved for
        // every apply (not only grip drags) so the non-grip Properties path can
        // preserve raw coordinates the way the grip path already does.
        //
        // ONE TRUTH (post-merge remediation of #493, round 2 — Codex P2 #515) —
        // resolved through the SAME `resolveGripDragDocumentEntity`
        // (gripDragTranslate.ts) the shell reducer (`propertyReducer.ts`) uses
        // for the identical `properties/apply` command: a `document.spaces`
        // linear scan first (full precision, authoritative when it finds the
        // entity — `document` is this reduce's pre-mutation-for-`entityId`
        // accumulator, equivalent to `geometryDocument` since no prior
        // iteration touches the same id), falling back to the injected
        // `resolveDocumentEntity` (`getEntityById`, compact-cache first) ONLY
        // when the scan misses a large/streamed import's slimmed `spaces`.
        // Without this fallback the PERSISTED document reducer could miss an
        // entity the shell reducer (already resolver-aware) resolved fine,
        // silently no-opping the typed writer below and diverging the
        // Properties panel from the geometry document / export / undo.
        const preMutationDocEntity = resolveGripDragDocumentEntity(
          entityId,
          document,
          resolveDocumentEntity,
        );
        const effectiveValues = isGripDragPayload(rawValues)
          ? (translateGripDrag(
              previewEntity,
              rawValues,
              preMutationDocEntity,
            ) ?? {})
          : rawValues;
        const allChanges = mergePropertyValueMap(effectiveValues);
        // DEFUSE S15 Color cutover — the elaborate EPIC-COLOR-UNION lossy-vs-
        // lossless SUPPRESSION BLOCK that used to live here (drop an UNCHANGED
        // incidental Color from a multi-field apply so an unrelated edit could not
        // clobber a rich stored colour) is DELETED. It is FULLY SUBSUMED by the
        // typed Color writer's DECISION PROCEDURE (`decideColorEdit`, Step 1c —
        // rawEqual → semantic-equal → sole-apply / multi-redisplay-suppress), fed
        // the SAME sole-vs-multi context the block read (`isSoleColorEdit` below ==
        // its `hasOtherChanges === false`):
        //   • MULTI mode is byte-compatible with the block: an incidental lossy
        //     redisplay suppresses via the same codec-normalized display compare
        //     (its lossy branch), a lossless `!EC:` payload no-ops only on exact
        //     structural identity (its serialize-compare branch), and a GENUINE
        //     colour change inside a multi-field draft applies;
        //   • SOLE mode FIXES the block's known edge (it always-applied sole
        //     edits): a rich union re-fed its OWN lossy display now no-ops via
        //     SEMANTIC colour equality (`semanticColorEqual` — 420 wins; the
        //     62/430 siblings are round-trip data, not the denoted colour), so
        //     `#FF8000`/`ff8000`/`255,128,0` re-submits preserve a stored rgb+aci;
        //     while every DELIBERATE clear still applies — `Color=ByLayer` clears a
        //     fallback-less bookColor, `Color=3` on a bookColor with fallback 3
        //     clears the 430 book metadata to the plain ACI (@codex P2 ×3 #447).
        //
        // INTENDED DIVERGENCE (data-preservation — the primary win, mirrors #446's
        // thickness): a rich colour re-fed a value DENOTING ITS OWN colour
        // (semantic-equal) NO-OPS instead of degrading the stored 62/420/430 union
        // to the lossy re-parse the legacy sole path wrote. Everything else —
        // sentinel clears, ACI-fallback clears, genuine edits, incidental
        // redisplays — matches the legacy/suppression behaviour exactly.
        const effectiveChanges = allChanges;
        // The simple-geometry typed writer only recognizes entity-specific
        // Line/Circle/Arc geometry labels — pass only specific changes to it
        // (common object props keep flowing through Step 2's translator).
        const specificChanges = effectiveChanges.filter(
          (change) => !isCommonObjectProperty(change.name),
        );

        // Step 1: simple geometry path (Line/Circle/Arc) — DEFUSE S15 typed
        // writer. Edit the LIVE typed document entity IN PLACE via the S15
        // descriptor-driven writer, reading each field's FULL document precision
        // and preserving every untouched X/Y/Z + group-30/31 Z by construction.
        // This replaces `applySimpleGeometryPropertyChanges` (which re-derived the
        // patch from the 3-decimal rounded preview string) + the now-deleted
        // `reconcileLinePatchWithDocument` / `reconcileCircleArcPatchWithDocument`
        // (which un-rounded that patch). The raw-precision / Z-preserve invariant
        // (#317 residual R2) is now structural — see the command-engine parity
        // gate `typedPropertyWriter.parity.test.ts`. A non-simple kind (or a
        // no-op apply) yields `null` → the document is unchanged.
        const typedSimpleEdit: TypedFieldPatch | null = preMutationDocEntity
          ? buildTypedSimpleGeometryEdit(preMutationDocEntity, specificChanges)
          : null;
        let nextDocument = applySimpleGeometryPatch(document, typedSimpleEdit);

        // Step 1b: common object properties (Layer/Linetype/Lineweight/
        // Transparency/Linetype Scale/Thickness) — DEFUSE S15 common-props
        // cutover. Route the 6 NON-Color common fields through the typed writer
        // (`buildTypedCommonEdit`) instead of the legacy string-bag
        // `mergeCommonFieldsForType` (Step 2's translator), so raw-precision /
        // absent-optional preservation holds by construction and there is ONE
        // writer per field. Gated by the SAME per-target CHPROP predicate the
        // legacy common merge applied (`canApplyProperty` — capability list AND
        // per-entity exposure), so a capability-excluded field (Linetype on
        // MText/Hatch/Viewport, Thickness on Dimension/RevCloud) or an
        // optional-absent field the target does not expose (Linetype Scale on a
        // default entity) is skipped exactly as before. Color stays on the legacy
        // path (Step 2) until its own slice. The writer edits the pre-mutation
        // entity's common fields — disjoint from Step 1's geometry fields — so the
        // shallow-merge patch preserves the Line/Circle/Arc geometry edit above.
        const commonCutoverChanges = effectiveChanges.filter(
          (change) =>
            TYPED_COMMON_CUTOVER_LABELS.has(change.name) &&
            canApplyProperty(previewEntity, change.name, "chprop"),
        );
        const typedCommonEdit: TypedFieldPatch | null = preMutationDocEntity
          ? buildTypedCommonEdit(preMutationDocEntity, commonCutoverChanges)
          : null;
        nextDocument = applySimpleGeometryPatch(nextDocument, typedCommonEdit);

        // Step 1c: Color common object property — DEFUSE S15 Color cutover. Route
        // Color through its OWN dedicated typed writer (`buildTypedColorEdit`,
        // deserializeColor toRaw + the `decideColorEdit` decision procedure)
        // instead of the legacy string-bag `mergeCommonFieldsForType` (Step 2's
        // translator). The decision procedure FULLY subsumes the deleted
        // suppression block (see the block-deletion note above): multi-field
        // incidental redisplays suppress via the same codec-normalized display
        // compare, lossless `!EC:` payloads no-op only on exact identity, sole
        // deliberate edits always land — with SEMANTIC colour equality preserving
        // a rich union re-fed a value denoting its own colour. Gated by the SAME
        // per-target CHPROP predicate the legacy common merge applied
        // (`canApplyProperty` — true for all 17 kinds, so the gate never narrows;
        // it is present so the shell↔document exposure cannot diverge). Color is a
        // separate writer (NOT in TYPED_COMMON_CUTOVER_LABELS): folding it into
        // `buildTypedCommonEdit` would hit that writer's `change.name === "Color"`
        // guard → silent no-op. The Color field is disjoint from Step 1's geometry
        // fields and Step 1b's common fields, so the shallow-merge patch preserves
        // both edits above.
        //
        // `isSoleColorEdit` = the per-entity EFFECTIVE change set contains ONLY
        // Color — computed from the SAME merged change set the deleted suppression
        // block inspected (its `hasOtherChanges = allChanges.some(name!=="Color")`,
        // negated), BEFORE any strip. A sole apply is a deliberate colour command
        // (CLI CHPROP / picker single-set) → clears always land; a multi-field
        // draft carries the palette's full redisplay → unchanged colours suppress.
        const isSoleColorEdit = effectiveChanges.every(
          (change) => change.name === "Color",
        );
        const colorCutoverChanges = effectiveChanges.filter(
          (change) =>
            change.name === "Color" &&
            canApplyProperty(previewEntity, "Color", "chprop"),
        );
        const typedColorEdit: TypedFieldPatch | null = preMutationDocEntity
          ? buildTypedColorEdit(preMutationDocEntity, colorCutoverChanges, {
              isSoleColorEdit,
            })
          : null;
        nextDocument = applySimpleGeometryPatch(nextDocument, typedColorEdit);

        // Step 1d: extended geometry (Rectangular Associative Array /
        // LWPOLYLINE — Slice A; Point / Ellipse — Slice B) — DEFUSE S15 typed
        // cutover. Edit the LIVE typed document entity through the
        // descriptor-driven writer (`buildTypedExtendedGeometryEdit`).
        // Slice A REPLACED the two DELETED
        // `translateExtendedEntityPropertyChanges` per-kind branches (ArrayRect
        // count/spacing/base/axis/associative + LWPOLYLINE vertices/closed/
        // bulges — plus Constant width / Elevation / Linetype generation, which
        // the deleted LWPOLYLINE branch silently dropped). Slice B is ADDITIVE
        // first-time writability: Point Position X/Y/Z + Rotation and Ellipse
        // Center/Major axis/Ratio/parameters never had a legacy write path at
        // all (absent from `patchablePropertyLookup` + `labelToFieldByType`;
        // the generic tail dropped them), so there is no branch to delete and
        // no double-write risk. Mirrors Step 1's shape exactly: the SAME
        // `specificChanges` filter (commons land via Step 1b/1c), the same
        // `applySimpleGeometryPatch` shallow-merge write. The builder reads
        // per-vertex bulge/widths/vertexId + point Z from the PRE-mutation
        // document entity, so raw precision / Z-preserve / metadata-preserve
        // hold by construction (the deleted branches needed `readRawPoint` +
        // zip-with-document to retrofit this). The touched top-level fields
        // (vertices/closed/rowCount/…/basePoint/location/rotation/center/
        // majorAxisEndpoint/ratio/startParam/endParam) are DISJOINT from Step
        // 1b/1c's common fields, so the shallow merges compose.
        // Parity + Blocker-A gates: command-engine
        // `typedPropertyWriter.extendedGeometry.test.ts` + apps/web
        // `extGeomSliceA.parity.test.ts` + the Slice-B DXF-group-code SPEC
        // oracle `extGeomSliceB.parity.test.ts` (no legacy oracle exists for
        // Point/Ellipse — they had no legacy write path).
        const typedExtendedEdit: TypedFieldPatch | null = preMutationDocEntity
          ? buildTypedExtendedGeometryEdit(preMutationDocEntity, specificChanges)
          : null;
        nextDocument = applySimpleGeometryPatch(nextDocument, typedExtendedEdit);

        // Step 1d-se: extended-geometry SIDE-EFFECT cutover (DEFUSE S15 Slice E —
        // the PIVOT). A descriptor whose write rebuilds COUPLED fields (HATCH
        // Pattern → patternName + solidFill + gradient + customPatternLines) is
        // SURFACED by the writer as a `side-effect` directive and routed through
        // the registered mutator (`hatchPatternMutator`) — never a plain field
        // set. `buildTypedExtendedGeometrySideEffectPatch` reads the PRE-mutation
        // document entity (so nameChanged / preserve-source hold), applies each
        // directive via the registry (throwing LOUDLY on an unregistered mutator),
        // and diffs the coupled fields into a patch. The touched fields
        // (patternName / solidFill / gradient / customPatternLines) are DISJOINT
        // from Step 1d's plain Hatch fields (patternScale / patternAngle /
        // associative / elevation) and Step 1b/1c's common fields, so the shallow
        // merges compose. This REPLACES the deleted `propertyTranslator` HATCH
        // Pattern-derivation branch (no double-write: `stripTypedExtendedGeometry-
        // Changes` also removes the side-effect labels before the translator).
        // @codex E (S15 Slice F) — cross-STEP dependency: a side-effect mutator
        // that DERIVES a coupled field from a PLAIN LEAF edited in the SAME apply
        // (Viewport `viewHeight` = height × ratio ← the plain `Height` leaf Step 1d
        // just wrote) must read the ALREADY-applied plain value, not the stale
        // pre-mutation one (Height=175 + Scale=1:50 → viewHeight 8750, not 5000).
        // Re-locate the entity from `nextDocument` (carries Step 1/1b/1c/1d edits)
        // as the mutation BASE, while threading `preMutationDocEntity` as the
        // genuine-change verdict base (so changedSiblingLabels + the changed-value
        // guard stay computed vs the ORIGINAL field — the Spline gated cell relies
        // on a Step-1d-applied Control-points edit still reading as "changed").
        let postPlainDocEntity: DrawingEntity | undefined;
        for (const space of nextDocument.spaces) {
          const found = space.entities.find((e) => e.id === entityId);
          if (found) {
            postPlainDocEntity = found;
            break;
          }
        }
        const typedSideEffectEdit: TypedFieldPatch | null =
          preMutationDocEntity && postPlainDocEntity
            ? buildTypedExtendedGeometrySideEffectPatch(
                postPlainDocEntity,
                specificChanges,
                preMutationDocEntity,
              )
            : null;
        nextDocument = applySimpleGeometryPatch(nextDocument, typedSideEffectEdit);

        // POST-Step-1d VIEWPORT normalizations (two ordered coupled-field
        // consistency passes; both idempotent + value-compared so nothing
        // double-writes). Gated on the viewport being touched by a typed step.
        const relocateVp = (): ViewportEntity | undefined => {
          for (const space of nextDocument.spaces) {
            const found = space.entities.find((e) => e.id === entityId);
            if (found?.type === "Viewport") return found as ViewportEntity;
          }
          return undefined;
        };
        if (
          (typedExtendedEdit || typedSideEffectEdit) &&
          postPlainDocEntity?.type === "Viewport"
        ) {
          // (1) @codex round-4 P2-3 — a STANDALONE plain `Height` edit (group 41)
          // on a viewport that carries an AUTHORITATIVE `vpOverrideScale` must
          // RE-DERIVE `viewHeight = height × (denom/num)` so the DISPLAYED scale
          // (`height/viewHeight`) stays consistent with the override the DXF export
          // writes (else a save/reload emits a scale that disagrees with the resized
          // paper height). Gated on: the override present, the plain step actually
          // changed `height`, AND the side-effect step did NOT already set
          // `viewHeight` (a same-apply Height+Scale/View-height lets the mutator's
          // own derivation win — the @codex-E fix — so we never double-derive). No
          // override ⇒ `viewHeight` untouched (the effective scale is derived and
          // stays consistent by construction). Runs BEFORE the clip re-project below,
          // which reads `viewHeight`.
          const heightChanged =
            typedExtendedEdit !== null &&
            Object.prototype.hasOwnProperty.call(typedExtendedEdit.changes, "height");
          const viewHeightSetBySideEffect =
            typedSideEffectEdit !== null &&
            Object.prototype.hasOwnProperty.call(
              typedSideEffectEdit.changes,
              "viewHeight",
            );
          const vpBeforeNorm = relocateVp();
          if (
            vpBeforeNorm?.vpOverrideScale &&
            vpBeforeNorm.vpOverrideScale.num > 0 &&
            heightChanged &&
            !viewHeightSetBySideEffect
          ) {
            const rederivedViewHeight =
              vpBeforeNorm.height *
              (vpBeforeNorm.vpOverrideScale.denom / vpBeforeNorm.vpOverrideScale.num);
            if (
              Number.isFinite(rederivedViewHeight) &&
              rederivedViewHeight !== vpBeforeNorm.viewHeight
            ) {
              nextDocument = applySimpleGeometryPatch(nextDocument, {
                entityId,
                changes: { viewHeight: rederivedViewHeight } as Partial<DrawingEntity>,
              });
            }
          }

          // (2) @codex P2-1 (round 3) — a CLIPPED viewport's native model-space
          // `clipPath` is DERIVED from its frame/view fields (center / height /
          // viewTwistAngle / viewCenter / viewHeight, via `paperToViewportModel`).
          // The side-effect view/scale mutators already re-project it, but a PURE
          // plain-leaf frame edit (Center X/Y / Height / View twist through Step 1d)
          // does NOT — leaving the clip stale (wrong model-space clip render /
          // export) until a later pan/scale. Re-project through the ONE-TRUTH
          // `recomputeNativeClipPath` (the SAME function the commands + mutators use,
          // reading the possibly-re-derived `viewHeight` from pass 1). Returns null
          // for an UNCLIPPED viewport (no `clipBoundaryPaperSpace`) → frame fields
          // stay simple scalars there; value-compared so a scale/view edit that
          // ALREADY recomputed does not double-write.
          const vpForClip = relocateVp();
          if (vpForClip) {
            const reprojected = recomputeNativeClipPath(vpForClip);
            if (reprojected) {
              const prev = vpForClip.clipPath;
              const changed =
                !prev ||
                prev.length !== reprojected.length ||
                reprojected.some(
                  (p, i) => p.x !== prev[i]!.x || p.y !== prev[i]!.y,
                );
              if (changed) {
                nextDocument = applySimpleGeometryPatch(nextDocument, {
                  entityId,
                  changes: { clipPath: reprojected } as Partial<DrawingEntity>,
                });
              }
            }
          }
        }

        // Step 2: extended entity types (Dimension/MText/Text/Multileader/etc.)
        // These don't go through `applySimpleGeometryPropertyChanges` —
        // they need a separate translation from property labels to entity
        // fields. ALL 7 common fields are now STRIPPED here before the
        // translator: the 6 non-Color labels via `stripTypedCommonCutoverChanges`
        // (landed via Step 1b) AND `Color` via `stripTypedColorChange` (landed via
        // Step 1c). Feeding either to `mergeCommonFieldsForType` would double-write
        // the typed document — and for Color specifically it would decode the LOSSY
        // display through `deserializeColor` and clobber the rich union Step 1c
        // preserved. (`mergeCommonFieldsForType` / `commonLabelToEntityField` stay
        // intact — inert for common labels now that all 7 are stripped, still used
        // by standalone/test callers; the full deletion is a deferred cleanup PR.)
        // Codex P1-B — locate the LIVE document entity so the translator
        // can source authoritative existing point counts / full-precision
        // values (Spline points, HATCH + generic doc-precision reconcile)
        // from the persisted model instead of the lossy preview. The typed
        // steps above (1/1b/1c/1d) touch only fields the remaining translator
        // branches never read for the same apply — the LWPOLYLINE per-vertex
        // consumer moved to Step 1d itself (S15 Slice A) — so reading from
        // `nextDocument` stays equivalent to the pre-mutation entity for the
        // attributes the translator consumes.
        let documentEntity: DrawingEntity | undefined;
        for (const space of nextDocument.spaces) {
          const found = space.entities.find((e) => e.id === entityId);
          if (found) {
            documentEntity = found;
            break;
          }
        }
        // DEFUSE S15 Slice A — additionally strip the extended-geometry labels
        // the typed writer now OWNS for this kind (ArrayRect / LWPOLYLINE
        // specials, landed via Step 1d) so the legacy translator can never
        // double-write them. For the cutover kinds the deleted branches leave
        // the generic tail with no mapping for these labels anyway
        // (`labelToFieldByType` has no ArrayRect/LWPOLYLINE entries) — the strip
        // is the by-construction guarantee. Non-cutover kinds pass through.
        const entityFieldPatch = translateExtendedEntityPropertyChanges(
          previewEntity,
          stripTypedExtendedGeometryChanges(
            previewEntity.type,
            stripTypedColorChange(stripTypedCommonCutoverChanges(effectiveChanges)),
          ),
          documentEntity,
        );
        if (entityFieldPatch) {
          // DYN-1.10: Block customProperties must be MERGED with the
          // existing map, not replaced. The translator emits a partial
          // customProperties map carrying only the keys the user
          // touched; a shallow merge would drop every other parameter
          // value. Detect this case by inspecting the patch shape and
          // pre-merge before feeding to patchEntityById.
          let mergedPatch = entityFieldPatch;
          const hasCustomPropsPatch =
            "customProperties" in entityFieldPatch &&
            entityFieldPatch.customProperties != null;
          if (previewEntity.type === "Block" && hasCustomPropsPatch) {
            // Locate the current BlockInsertEntity across spaces so we
            // can merge with its existing customProperties map.
            let existing:
              | Record<string, string | number | boolean>
              | undefined = undefined;
            for (const space of nextDocument.spaces) {
              const found = space.entities.find((e) => e.id === entityId);
              if (found && found.type === "Block") {
                existing = found.customProperties ?? {};
                break;
              }
            }
            const incoming =
              (
                entityFieldPatch as {
                  customProperties?: Record<string, string | number | boolean>;
                }
              ).customProperties ?? {};
            mergedPatch = {
              ...entityFieldPatch,
              customProperties: { ...(existing ?? {}), ...incoming },
            } as typeof entityFieldPatch;
          }
          // T6c incremental validation: the extended-entity field patch is a
          // single-entity in-place edit via `patchEntityById` (id/spaceId
          // preserved, no entity added/removed) → invariants preserved by
          // construction → fast-path with this `entityId`. The per-entity Zod
          // field check still runs in dev/test (the translated patch must pass
          // the schema — @codex #390-F4); the whole-document walk is dropped.
          nextDocument = assembleDrawingDocumentInPlace(
            patchEntityById(nextDocument, entityId, mergedPatch),
            [entityId],
          );
        }

        // POST-ALL-STEPS MTEXT normalization — @codex PR #794 round 14 P2.
        // Groups 42/43 (`horizontalWidth`/`verticalHeight`) are AutoCAD's OWN
        // MEASUREMENT of the laid-out block, not inputs, and AutoCAD recomputes
        // them on edit. Carrying an import-time measurement across a contents /
        // height / style / wrap-width / line-spacing change is stale data with
        // two readers: `mtextBoxSize`'s SEMANTIC rectangle takes group 42
        // verbatim as its width, and the EXACT select ring's right edge is
        // `min(walk, group 42)` (round 13) — against a stale measurement that
        // clamp truncates the ring to the OLD width while the renderer paints
        // the NEW string, so a crossing window over the visible suffix MISSES
        // the entity. The CAD rule (which fields invalidate, why both halves
        // always go together, why clearing rather than re-measuring) lives in
        // the package-level `command-engine::mtextLayoutMeasurementInvalidation`.
        //
        // HERE, after every builder, and keyed on a PREV→NEXT FIELD DIFF rather
        // than on one builder's patch: an MTEXT apply reaches the document
        // through the typed writer (Contents / Text style / Height / Width) AND
        // through the legacy translator (`Line spacing` → `lineSpacingFactor`
        // is cutover-DEFERRED), so a rule attached to either one alone would
        // leave the other's edit stale. Same reducer transition, same
        // `EntityMutation` — the `prev`/`next` pair carries the old measurement,
        // which is what the undo inverse restores from (`editorStore`'s
        // `properties/apply` snapshot sweep via `mtextLayoutMeasurementDropped`;
        // the label replay cannot restore a field with no property label).
        // Idempotent and value-compared: no layout-affecting field changed, or
        // no measurement carried, ⇒ no patch (the fast path for every other
        // apply). Shaped like the POST-Step-1d viewport normalizations above.
        if (preMutationDocEntity?.type === "MText") {
          let postStepsEntity: DrawingEntity | undefined;
          for (const space of nextDocument.spaces) {
            const found = space.entities.find((e) => e.id === entityId);
            if (found) {
              postStepsEntity = found;
              break;
            }
          }
          const invalidation = postStepsEntity
            ? mtextLayoutMeasurementInvalidation(
                preMutationDocEntity,
                postStepsEntity,
              )
            : null;
          if (invalidation) {
            nextDocument = assembleDrawingDocumentInPlace(
              patchEntityById(nextDocument, entityId, invalidation),
              [entityId],
            );
          }
        }

        return nextDocument;
      }, geometryDocument);
      return wrapHelperResult(geometryDocument, nextDoc);
    }

    case "shell/delete-entities": {
      // Construct mutations directly from the request — the deleted
      // ids are known up-front, no need to diff. This avoids
      // false-positive `updated` records from `assembleDrawingDocument`'s
      // Zod re-parse breaking reference identity on survivors.
      const removed: Array<{ spaceId: string; entityId: string }> = [];
      const idsToDelete = new Set(command.entityIds);
      for (const space of geometryDocument.spaces) {
        for (const entity of space.entities) {
          if (idsToDelete.has(entity.id)) {
            removed.push({ spaceId: space.id, entityId: entity.id });
          }
        }
      }
      const nextDoc = removeGeometryEntitiesById(
        geometryDocument,
        command.entityIds,
      );
      if (removed.length === 0) {
        return { document: nextDoc, mutations: NO_MUTATIONS };
      }
      // Referential cleanup (PR #318 R2): if any deleted entity was a viewport
      // clip boundary, clear the now-dangling 340 hard-pointer on the surviving
      // viewport(s) so the writer does not re-emit a dangling reference. The
      // per-VP diffs are threaded into `updated` so the incremental compact-cache
      // sync drops the stale handle too (not just the returned document).
      const { document: cleanedDoc, updated: clipUpdated } =
        clearDanglingViewportClips(nextDoc, command.entityIds);
      return {
        document: cleanedDoc,
        mutations: {
          added: [],
          removed,
          updated: clipUpdated,
          styleTablesTouched: NO_MUTATIONS.styleTablesTouched,
        },
      };
    }

    case "geometry/delete-entity": {
      // Phase 4.1.c.2 — single-entity delete; mirrors shell/delete-entities
      // but targets exactly one entity by id. Used as the inverse command for
      // geometry/create-* commands (computeInverse returns this type).
      const targetId = command.entityId;
      let foundSpaceId: string | undefined;
      for (const space of geometryDocument.spaces) {
        if (space.entities.some((e) => e.id === targetId)) {
          foundSpaceId = space.id;
          break;
        }
      }
      const nextDocSingle = removeGeometryEntitiesById(geometryDocument, [
        targetId,
      ]);
      if (foundSpaceId === undefined) {
        return { document: nextDocSingle, mutations: NO_MUTATIONS };
      }
      // Referential cleanup (PR #318 R2): if the deleted entity was a viewport
      // clip boundary, clear the now-dangling 340 hard-pointer on the surviving
      // viewport(s) so the writer does not re-emit a dangling reference. The
      // per-VP diffs are threaded into `updated` so the incremental compact-cache
      // sync drops the stale handle too (not just the returned document).
      const { document: cleanedDocSingle, updated: clipUpdatedSingle } =
        clearDanglingViewportClips(nextDocSingle, [targetId]);
      return {
        document: cleanedDocSingle,
        mutations: {
          added: [],
          removed: [{ spaceId: foundSpaceId, entityId: targetId }],
          updated: clipUpdatedSingle,
          styleTablesTouched: NO_MUTATIONS.styleTablesTouched,
        },
      };
    }

    case "geometry/restore-entity": {
      // Phase 4.1.c.2 follow-up (P1-5) — re-insert a previously deleted entity
      // with ALL its original fields preserved (id, layer, color, lineweight,
      // transparency, custom properties, etc.). Used as the inverse of
      // geometry/delete-entity emitted by computeInverse.
      //
      // The `entity` field is z.unknown() in the schema (to avoid importing
      // drawing-model from contracts); cast via DrawingEntityInput here where
      // drawing-model is already a dependency. appendGeometryEntity parses the
      // entity through drawingEntitySchema which fills in any defaults.
      const entityToRestore = command.entity as DrawingEntityInput;
      if (!entityToRestore || typeof entityToRestore !== "object") {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }
      const restoredDoc = appendGeometryEntity(
        geometryDocument,
        entityToRestore,
      );
      // Determine the entity id from the snapshot.
      const restoredId = (entityToRestore as { id?: string }).id;
      if (!restoredId) {
        return { document: restoredDoc, mutations: NO_MUTATIONS };
      }
      // singleAddedMutation resolves the entity reference from the post-insert
      // document so the mutations payload carries the canonical DrawingEntity
      // (required by commitMutation which calls toMetadata(m.entity, m.spaceId)).
      return {
        document: restoredDoc,
        mutations: singleAddedMutation(
          restoredDoc,
          command.spaceId,
          restoredId,
        ),
      };
    }

    case "shell/restore-entities": {
      // Phase 4.1.c.2 follow-up (P1-4) — re-insert a batch of previously
      // deleted entities with ALL their original fields preserved. Used as the
      // inverse of shell/delete-entities emitted by computeInverse.
      const entitiesToRestore = command.entities as DrawingEntityInput[];
      if (!Array.isArray(entitiesToRestore) || entitiesToRestore.length === 0) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }
      // T6a perf: append the whole restore batch with ONE assembleDrawingDocument
      // pass (appendGeometryEntities) instead of O(N) per entity (O(N²) over the
      // batch). Ids are preserved verbatim from the snapshots (no minting), and
      // the batch helper applies the SAME per-entity schema parse + missing-space
      // drop as the prior appendGeometryEntity loop — byte-identical output.
      const restoreInputs: DrawingEntityInput[] = [];
      const restoredIds: Array<{ spaceId: string; entityId: string }> = [];
      for (const entityInput of entitiesToRestore) {
        if (!entityInput || typeof entityInput !== "object") continue;
        restoreInputs.push(entityInput);
        const entityId = (entityInput as { id?: string }).id;
        // Each entity carries its own spaceId — use it (falls back to command.spaceId).
        const entitySpaceId =
          (entityInput as { spaceId?: string }).spaceId ?? command.spaceId;
        if (entityId) {
          restoredIds.push({ spaceId: entitySpaceId, entityId });
        }
      }
      const restoredDocBatch = appendGeometryEntities(
        geometryDocument,
        restoreInputs,
      );
      if (restoredIds.length === 0) {
        return { document: restoredDocBatch, mutations: NO_MUTATIONS };
      }
      // Build added array using singleAddedMutation for each entity to get
      // the correct { spaceId, entity: DrawingEntity } shape required by commitMutation.
      const addedPayloads: EntityMutation["added"][number][] = [];
      for (const { spaceId, entityId } of restoredIds) {
        const mut = singleAddedMutation(restoredDocBatch, spaceId, entityId);
        if (mut.added.length > 0) {
          addedPayloads.push(mut.added[0]!);
        }
      }
      if (addedPayloads.length === 0) {
        return { document: restoredDocBatch, mutations: NO_MUTATIONS };
      }
      return {
        document: restoredDocBatch,
        mutations: {
          added: addedPayloads,
          removed: [],
          updated: [],
          styleTablesTouched: NO_MUTATIONS.styleTablesTouched,
        },
      };
    }

    case "geometry/restore-viewport-clip": {
      // PR #324 / #318 R2 — in-place restore of a surviving viewport's cleared
      // clip (the inverse half of a delete that erased a clip boundary). For
      // each { spaceId, viewport } entry, REPLACE the existing entity matched by
      // viewport.id in that space with the full pre-delete snapshot (restoring
      // clippingBoundaryHandle + group-90 0x10000 bit + legacy extraGroups-340).
      // This is a REPLACE, never an append — appending would duplicate the id.
      // Each replacement is emitted as an `updated` diff so the compact-cache
      // sync re-learns the restored clip handle (mirrors why
      // clearDanglingViewportClips threads `updated`: without it the store stays
      // cleared even though the document is restored).
      const entries = command.viewports as Array<{
        spaceId: string;
        viewport: DrawingEntityInput;
      }>;
      if (!Array.isArray(entries) || entries.length === 0) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }
      let nextDocClip = geometryDocument;
      // @codex #496 P2 — the VPCLIP set/set-polygon/remove forwards ALSO strip the
      // imported SPATIAL_FILTER OBJECTS chain from the document's verbatim
      // `rawObjectGroups` (detachInvalidatedViewportClipObjects) — a document-scope
      // mutation the per-viewport entity restore above cannot reach. When the
      // inverse carries the PRE-mutation `rawObjectGroups` snapshot, re-attach it
      // VERBATIM so a strip → undo → save round-trips the removed OBJECTS records
      // (else the restored viewport's 340/ext-dict handle dangles on reload). Only
      // present when the forward actually stripped a chain; absent otherwise, so
      // rawObjectGroups is left untouched for clip commands with no imported chain.
      const restoreRawObjectGroups = (
        command as { restoreRawObjectGroups?: Array<{ code: number; value: string }> }
      ).restoreRawObjectGroups;
      if (Array.isArray(restoreRawObjectGroups)) {
        nextDocClip = {
          ...nextDocClip,
          rawObjectGroups: restoreRawObjectGroups.map((g) => ({
            code: g.code,
            value: g.value,
          })),
        };
      }
      // REPLACE each survivor in place through the shared snapshot-restore
      // truth (`replaceEntitySnapshots`): parse → replace-by-id (never append)
      // → one `updated` diff per replacement. `geometry/restore-entity-snapshot`
      // calls the SAME helper; the two arms differ only in the entry field name
      // and the rawObjectGroups re-attach above.
      const { document: replacedClipDoc, updated } = replaceEntitySnapshots(
        nextDocClip,
        entries.map((entry) => ({
          spaceId: entry?.spaceId,
          snapshot: entry?.viewport,
        })),
      );
      nextDocClip = replacedClipDoc;
      if (updated.length === 0) {
        // No viewport entity changed — but if the rawObjectGroups snapshot was
        // re-attached (nextDocClip diverged), return THAT document so the OBJECTS
        // restore is not silently discarded. A rawObjectGroups change is a
        // document-scope field, not an entity diff, so no entity mutation is
        // emitted (the compact-cache tracks entities only).
        return {
          document: nextDocClip,
          mutations: NO_MUTATIONS,
        };
      }
      return {
        document: nextDocClip,
        mutations: {
          added: [],
          removed: [],
          updated,
          styleTablesTouched: NO_MUTATIONS.styleTablesTouched,
        },
      };
    }

    case "geometry/restore-entity-snapshot": {
      // @codex PR #785 round 2, F2 — in-place, full-typed-snapshot REPLACE of an
      // EXISTING entity, the PRESENCE-BIT half of the `properties/apply`
      // inverse. The display-string replay restores a field's VALUE but cannot
      // restore the difference between an ABSENT optional DXF group and one
      // written with the value its absence displays (an imported heavy POLYLINE
      // with no group 40 shows the inherited width 0), so when an apply makes a
      // previously-absent field present the editorStore composes the entity's
      // full pre-edit snapshot into the inverse and this arm swaps the entity
      // back verbatim.
      //
      // REPLACE by id, never an append (appending would duplicate the id), and
      // each replacement is emitted as an `updated` diff so the compact-cache
      // sync re-learns the restored state — the same one-truth helper the
      // `geometry/restore-viewport-clip` arm above calls. This command carries
      // NO document-scope payload: the OBJECTS re-attach is specific to the
      // viewport-clip delete inverse.
      const snapshotEntries = command.entities as Array<{
        spaceId: string;
        entity: DrawingEntityInput;
      }>;
      if (!Array.isArray(snapshotEntries) || snapshotEntries.length === 0) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }
      const { document: restoredSnapshotDoc, updated: snapshotUpdated } =
        replaceEntitySnapshots(
          geometryDocument,
          snapshotEntries.map((entry) => ({
            spaceId: entry?.spaceId,
            snapshot: entry?.entity,
          })),
        );
      if (snapshotUpdated.length === 0) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }
      return {
        document: restoredSnapshotDoc,
        mutations: {
          added: [],
          removed: [],
          updated: snapshotUpdated,
          styleTablesTouched: NO_MUTATIONS.styleTablesTouched,
        },
      };
    }

    default:
      return null;
  }
}
